// ============================================================
// bankSoalFigural.js — Bank Soal Figural & Spasial TNI
// Semua soal menggunakan SVG inline sebagai stimulus visual
// Jenis: Rotasi, Bayangan Cermin, Pola Matriks, Deret Gambar
// ============================================================

// ─── SVG Builder Helpers ─────────────────────────────────────
const W = 80; // viewBox width
const H = 80; // viewBox height

function svgWrap(content, bg = '#F8FAFC') {
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="${W}" height="${H}" fill="${bg}" rx="8"/>
    ${content}
  </svg>`;
}

// Bentuk dasar
const shapes = {
  // Segitiga atas
  triUp: (x, y, s, fill='#3B82F6', stroke='#1D4ED8') =>
    `<polygon points="${x},${y-s} ${x-s},${y+s} ${x+s},${y+s}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Segitiga bawah
  triDown: (x, y, s, fill='#3B82F6', stroke='#1D4ED8') =>
    `<polygon points="${x},${y+s} ${x-s},${y-s} ${x+s},${y-s}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Segitiga kanan
  triRight: (x, y, s, fill='#3B82F6', stroke='#1D4ED8') =>
    `<polygon points="${x+s},${y} ${x-s},${y-s} ${x-s},${y+s}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Segitiga kiri
  triLeft: (x, y, s, fill='#3B82F6', stroke='#1D4ED8') =>
    `<polygon points="${x-s},${y} ${x+s},${y-s} ${x+s},${y+s}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Kotak
  rect: (x, y, w, h, fill='#10B981', stroke='#047857') =>
    `<rect x="${x-w/2}" y="${y-h/2}" width="${w}" height="${h}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Lingkaran
  circle: (x, y, r, fill='#F59E0B', stroke='#D97706') =>
    `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Belah ketupat
  diamond: (x, y, s, fill='#8B5CF6', stroke='#6D28D9') =>
    `<polygon points="${x},${y-s} ${x+s},${y} ${x},${y+s} ${x-s},${y}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`,
  // Bintang
  star: (x, y, r, fill='#EF4444', stroke='#B91C1C') => {
    const pts = [];
    for (let i = 0; i < 5; i++) {
      const a1 = (i * 72 - 90) * Math.PI / 180;
      const a2 = (i * 72 - 54) * Math.PI / 180;
      pts.push(`${(x + r * Math.cos(a1)).toFixed(1)},${(y + r * Math.sin(a1)).toFixed(1)}`);
      pts.push(`${(x + r/2 * Math.cos(a2)).toFixed(1)},${(y + r/2 * Math.sin(a2)).toFixed(1)}`);
    }
    return `<polygon points="${pts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
  },
  // Panah kanan
  arrowRight: (x, y, s, fill='#6366F1', stroke='#4338CA') =>
    `<polygon points="${x-s},${y-s/2} ${x},${y-s/2} ${x},${y-s} ${x+s},${y} ${x},${y+s} ${x},${y+s/2} ${x-s},${y+s/2}" fill="${fill}" stroke="${stroke}" stroke-width="1"/>`,
  // Plus
  plus: (x, y, s, fill='#14B8A6', stroke='#0F766E') =>
    `<path d="M${x-s/3},${y-s} h${s*2/3} v${s*2/3} h${s*2/3} v${s*2/3} h-${s*2/3} v${s*2/3} h-${s*2/3} v-${s*2/3} h-${s*2/3} v-${s*2/3} h${s*2/3} z" fill="${fill}" stroke="${stroke}" stroke-width="1"/>`,
  // Garis diagonal
  diagLine: (x1, y1, x2, y2, color='#374151') =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="2.5" stroke-linecap="round"/>`,
  // Titik/dot
  dot: (x, y, r=3, fill='#374151') =>
    `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`,
  // Teks
  text: (x, y, t, size=12, fill='#374151') =>
    `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" font-size="${size}" font-weight="bold" fill="${fill}">${t}</text>`,
};

// ─── Cluster helpers (dipakai soal "cocokkan tekstur" — DGM-016 s/d 019) ──
// Menempatkan 1-5 elemen kecil dalam susunan tetap di sekitar (cx,cy),
// dipakai untuk soal berbasis hitung jumlah elemen per kotak.
const CLUSTER_OFFSETS = {
  1: [[0,0]], 2: [[-4,0],[4,0]], 3: [[-4,-3],[4,-3],[0,4]],
  4: [[-4,-4],[4,-4],[-4,4],[4,4]], 5: [[-4,-4],[4,-4],[-4,4],[4,4],[0,0]],
};
function clusterDots(cx, cy, n, color) {
  return CLUSTER_OFFSETS[n].map(([dx,dy]) => `<circle cx="${cx+dx}" cy="${cy+dy}" r="2.6" fill="${color}"/>`).join('');
}
function clusterSquares(cx, cy, n, color) {
  return CLUSTER_OFFSETS[n].map(([dx,dy]) => `<rect x="${cx+dx-2.2}" y="${cy+dy-2.2}" width="4.4" height="4.4" fill="${color}"/>`).join('');
}
function clusterTri(cx, cy, n, color) {
  return CLUSTER_OFFSETS[n].map(([dx,dy]) => `<polygon points="${cx+dx},${cy+dy-3} ${cx+dx-3},${cy+dy+3} ${cx+dx+3},${cy+dy+3}" fill="${color}"/>`).join('');
}
function clusterPlus(cx, cy, n, color) {
  return CLUSTER_OFFSETS[n].map(([dx,dy]) => `<path d="M${cx+dx-1},${cy+dy-3} h2 v2 h2 v2 h-2 v2 h-2 v-2 h-2 v-2 h2 z" fill="${color}"/>`).join('');
}

// ─── Pie-slice helper (dipakai soal "transformasi bentuk" — MAT-046) ─────
// Menggambar lingkaran yang terisi sebagian (0-360 derajat) untuk soal
// pola transformasi bertahap (kosong -> 1/4 -> 1/2 -> 3/4 -> penuh).
function pieSlice(cx, cy, r, angleDeg, fill, stroke) {
  if (angleDeg <= 0) return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${stroke}" stroke-width="1.5"/>`;
  if (angleDeg >= 360) return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
  const startA = -90;
  const endA = startA + angleDeg;
  const toRad = d => d * Math.PI / 180;
  const x1 = cx + r * Math.cos(toRad(startA)), y1 = cy + r * Math.sin(toRad(startA));
  const x2 = cx + r * Math.cos(toRad(endA)), y2 = cy + r * Math.sin(toRad(endA));
  const largeArc = angleDeg > 180 ? 1 : 0;
  return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${stroke}" stroke-width="1.5"/><path d="M${cx},${cy} L${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${largeArc} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z" fill="${fill}" stroke="none"/>`;
}

// ─── 1. ROTASI BANGUN ─────────────────────────────────────────
export const soalRotasi = [
  {
    id: 'ROT-001',
    jenis: 'rotasi',
    soal: 'Objek berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triUp(40, 40, 22)),
    pilihan: {
      a: svgWrap(shapes.triRight(40, 40, 22)),
      b: svgWrap(shapes.triDown(40, 40, 22)),
      c: svgWrap(shapes.triLeft(40, 40, 22)),
      d: svgWrap(shapes.triUp(40, 40, 22)),
    },
    jawaban: 'a',
    pembahasan: 'Segitiga atas diputar 90° CW → menjadi segitiga kanan',
  },
  {
    id: 'ROT-002',
    jenis: 'rotasi',
    soal: 'Objek berikut diputar 180°. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triRight(40, 40, 22)),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 22)),
      b: svgWrap(shapes.triLeft(40, 40, 22)),
      c: svgWrap(shapes.triDown(40, 40, 22)),
      d: svgWrap(shapes.triRight(40, 40, 22)),
    },
    jawaban: 'b',
    pembahasan: 'Segitiga kanan diputar 180° → menjadi segitiga kiri',
  },
  {
    id: 'ROT-003',
    jenis: 'rotasi',
    soal: 'Objek berikut diputar 90° berlawanan jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triDown(40, 40, 22)),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 22)),
      b: svgWrap(shapes.triRight(40, 40, 22)),
      c: svgWrap(shapes.triUp(40, 40, 22)),
      d: svgWrap(shapes.triDown(40, 40, 22)),
    },
    jawaban: 'b',
    pembahasan: 'Segitiga bawah diputar 90° CCW → menjadi segitiga kanan',
  },
  {
    id: 'ROT-004',
    jenis: 'rotasi',
    soal: 'Panah berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(
      `<polygon points="40,15 55,40 47,40 47,65 33,65 33,40 25,40" fill="#6366F1" stroke="#4338CA" stroke-width="1.5"/>`
    ),
    pilihan: {
      a: svgWrap(`<polygon points="15,40 40,25 40,33 65,33 65,47 40,47 40,55" fill="#6366F1" stroke="#4338CA" stroke-width="1.5"/>`),
      b: svgWrap(`<polygon points="40,65 25,40 33,40 33,15 47,15 47,40 55,40" fill="#6366F1" stroke="#4338CA" stroke-width="1.5"/>`),
      c: svgWrap(`<polygon points="65,40 40,55 40,47 15,47 15,33 40,33 40,25" fill="#6366F1" stroke="#4338CA" stroke-width="1.5"/>`),
      d: svgWrap(`<polygon points="40,15 55,40 47,40 47,65 33,65 33,40 25,40" fill="#6366F1" stroke="#4338CA" stroke-width="1.5"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Panah atas diputar 90° CW → panah ke kanan',
  },
  {
    id: 'ROT-005',
    jenis: 'rotasi',
    soal: 'Huruf "L" berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(`<path d="M30,15 h12 v38 h20 v12 h-32 z" fill="#10B981" stroke="#047857" stroke-width="1.5"/>`),
    pilihan: {
      a: svgWrap(`<path d="M15,30 h38 v12 h-26 v20 h-12 z" fill="#10B981" stroke="#047857" stroke-width="1.5"/>`),
      b: svgWrap(`<path d="M50,15 h-12 v38 h-20 v12 h32 z" fill="#10B981" stroke="#047857" stroke-width="1.5"/>`),
      c: svgWrap(`<path d="M65,50 h-38 v-12 h26 v-20 h12 z" fill="#10B981" stroke="#047857" stroke-width="1.5"/>`),
      d: svgWrap(`<path d="M30,15 h12 v38 h20 v12 h-32 z" fill="#EF4444" stroke="#B91C1C" stroke-width="1.5"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Huruf L diputar 90° CW menghasilkan bentuk seperti "⌐"',
  },
  {
    id: 'ROT-006',
    jenis: 'rotasi',
    soal: 'Objek dengan 2 bentuk berikut diputar 90° CW. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triUp(28, 35, 15) + shapes.rect(55, 45, 18, 18)),
    pilihan: {
      a: svgWrap(shapes.triRight(45, 28, 15) + shapes.rect(35, 55, 18, 18)),
      b: svgWrap(shapes.triDown(28, 35, 15) + shapes.rect(55, 45, 18, 18)),
      c: svgWrap(shapes.triLeft(45, 52, 15) + shapes.rect(35, 25, 18, 18)),
      d: svgWrap(shapes.triUp(55, 35, 15) + shapes.rect(28, 45, 18, 18)),
    },
    jawaban: 'a',
    pembahasan: 'Saat diputar 90° CW, segitiga atas → kanan, posisi relatif berubah sesuai rotasi',
  },
  {
    id: 'ROT-007',
    jenis: 'rotasi',
    soal: 'Bintang berikut diputar 180°. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.star(30, 30, 18) + shapes.dot(58, 58, 6, '#EF4444')),
    pilihan: {
      a: svgWrap(shapes.star(50, 50, 18) + shapes.dot(22, 22, 6, '#EF4444')),
      b: svgWrap(shapes.star(30, 50, 18) + shapes.dot(58, 22, 6, '#EF4444')),
      c: svgWrap(shapes.star(50, 30, 18) + shapes.dot(22, 58, 6, '#EF4444')),
      d: svgWrap(shapes.star(30, 30, 18) + shapes.dot(58, 58, 6, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 180° membalik posisi: kiri-atas ↔ kanan-bawah',
  },
  {
    id: 'ROT-008',
    jenis: 'rotasi',
    soal: 'Huruf "F" berikut diputar 90° berlawanan jarum jam. Hasilnya?',
    stimulus: svgWrap(`<path d="M25,15 h30 v10 h-18 v8 h15 v10 h-15 v22 h-12 z" fill="#8B5CF6" stroke="#6D28D9" stroke-width="1.5"/>`),
    pilihan: {
      a: svgWrap(`<path d="M15,55 v-30 h10 v18 h8 v-15 h10 v15 h22 v12 z" fill="#8B5CF6" stroke="#6D28D9" stroke-width="1.5"/>`),
      b: svgWrap(`<path d="M55,65 h-30 v-10 h18 v-8 h-15 v-10 h15 v-22 h12 z" fill="#8B5CF6" stroke="#6D28D9" stroke-width="1.5"/>`),
      c: svgWrap(`<path d="M65,25 v30 h-10 v-18 h-8 v15 h-10 v-15 h-22 v-12 z" fill="#8B5CF6" stroke="#6D28D9" stroke-width="1.5"/>`),
      d: svgWrap(`<path d="M25,15 h30 v10 h-18 v8 h15 v10 h-15 v22 h-12 z" fill="#F59E0B" stroke="#D97706" stroke-width="1.5"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Huruf F diputar 90° CCW menghasilkan bentuk horizontal menghadap bawah',
  },
  {
    id: 'ROT-009',
    jenis: 'rotasi',
    soal: 'Objek berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triLeft(40, 40, 22)),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 22)),
      b: svgWrap(shapes.triRight(40, 40, 22)),
      c: svgWrap(shapes.triDown(40, 40, 22)),
      d: svgWrap(shapes.triLeft(40, 40, 22)),
    },
    jawaban: 'a',
    pembahasan: 'Segitiga kiri diputar 90° CW → menjadi segitiga atas',
  },
  {
    id: 'ROT-010',
    jenis: 'rotasi',
    soal: 'Kombinasi bentuk berikut diputar 180°. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.diamond(22, 25, 12, '#8B5CF6', '#6D28D9') + shapes.dot(58, 55, 6, '#EF4444')),
    pilihan: {
      a: svgWrap(shapes.diamond(58, 55, 12, '#8B5CF6', '#6D28D9') + shapes.dot(22, 25, 6, '#EF4444')),
      b: svgWrap(shapes.diamond(22, 25, 12, '#8B5CF6', '#6D28D9') + shapes.dot(58, 55, 6, '#EF4444')),
      c: svgWrap(shapes.diamond(58, 25, 12, '#8B5CF6', '#6D28D9') + shapes.dot(22, 55, 6, '#EF4444')),
      d: svgWrap(shapes.diamond(58, 55, 12, '#EF4444', '#B91C1C') + shapes.dot(22, 25, 6, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 180° membalik posisi sepenuhnya: kiri-atas ↔ kanan-bawah, bentuk & warna tetap',
  },
  {
    id: 'ROT-011',
    jenis: 'rotasi',
    soal: 'Objek berikut diputar 90° berlawanan jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triRight(40, 40, 22)),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 22)),
      b: svgWrap(shapes.triLeft(40, 40, 22)),
      c: svgWrap(shapes.triDown(40, 40, 22)),
      d: svgWrap(shapes.triRight(40, 40, 22)),
    },
    jawaban: 'a',
    pembahasan: 'Segitiga kanan diputar 90° CCW → menjadi segitiga atas',
  },
  {
    id: 'ROT-012',
    jenis: 'rotasi',
    soal: 'Kombinasi bentuk berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.rect(20, 25, 16, 16, '#10B981', '#047857') + shapes.triUp(58, 58, 12, '#F59E0B', '#D97706')),
    pilihan: {
      a: svgWrap(shapes.rect(25, 60, 16, 16, '#10B981', '#047857') + shapes.triRight(58, 22, 12, '#F59E0B', '#D97706')),
      b: svgWrap(shapes.rect(60, 25, 16, 16, '#10B981', '#047857') + shapes.triLeft(22, 58, 12, '#F59E0B', '#D97706')),
      c: svgWrap(shapes.rect(25, 60, 16, 16, '#F59E0B', '#D97706') + shapes.triRight(58, 22, 12, '#10B981', '#047857')),
      d: svgWrap(shapes.rect(25, 60, 16, 16, '#10B981', '#047857') + shapes.triDown(58, 22, 12, '#F59E0B', '#D97706')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 90° CW menggeser posisi dan mengubah orientasi segitiga (atas → kanan)',
  },
  {
    id: 'ROT-013',
    jenis: 'rotasi',
    soal: 'Kombinasi bentuk berikut diputar 90° searah jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.star(25, 58, 14, '#EF4444', '#B91C1C') + shapes.dot(58, 22, 5, '#3B82F6')),
    pilihan: {
      a: svgWrap(shapes.star(58, 55, 14, '#EF4444', '#B91C1C') + shapes.dot(22, 22, 5, '#3B82F6')),
      b: svgWrap(shapes.star(58, 55, 14, '#3B82F6', '#1D4ED8') + shapes.dot(22, 22, 5, '#EF4444')),
      c: svgWrap(shapes.star(22, 22, 14, '#EF4444', '#B91C1C') + shapes.dot(58, 55, 5, '#3B82F6')),
      d: svgWrap(shapes.star(58, 55, 14, '#EF4444', '#B91C1C') + shapes.dot(58, 22, 5, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 90° CW menggeser posisi kedua bentuk searah jarum jam mengelilingi pusat',
  },
  {
    id: 'ROT-014',
    jenis: 'rotasi',
    soal: 'Kotak berikut diputar 180°. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.rect(20, 58, 24, 12, '#3B82F6', '#1D4ED8')),
    pilihan: {
      a: svgWrap(shapes.rect(60, 22, 24, 12, '#3B82F6', '#1D4ED8')),
      b: svgWrap(shapes.rect(60, 58, 24, 12, '#3B82F6', '#1D4ED8')),
      c: svgWrap(shapes.rect(20, 22, 24, 12, '#3B82F6', '#1D4ED8')),
      d: svgWrap(shapes.rect(60, 22, 12, 24, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 180° memindahkan posisi ke sudut berlawanan, bentuk kotak tetap sama',
  },
  {
    id: 'ROT-015',
    jenis: 'rotasi',
    soal: 'Kombinasi bentuk berikut diputar 90° berlawanan jarum jam. Pilih hasil yang benar!',
    stimulus: svgWrap(shapes.triDown(58, 25, 12, '#F59E0B', '#D97706') + shapes.circle(22, 58, 8, '#8B5CF6', '#6D28D9')),
    pilihan: {
      a: svgWrap(shapes.triRight(55, 58, 12, '#F59E0B', '#D97706') + shapes.circle(22, 22, 8, '#8B5CF6', '#6D28D9')),
      b: svgWrap(shapes.triLeft(55, 58, 12, '#F59E0B', '#D97706') + shapes.circle(22, 22, 8, '#8B5CF6', '#6D28D9')),
      c: svgWrap(shapes.triRight(22, 22, 12, '#F59E0B', '#D97706') + shapes.circle(55, 58, 8, '#8B5CF6', '#6D28D9')),
      d: svgWrap(shapes.triRight(55, 58, 12, '#8B5CF6', '#6D28D9') + shapes.circle(22, 22, 8, '#F59E0B', '#D97706')),
    },
    jawaban: 'a',
    pembahasan: 'Rotasi 90° CCW: segitiga bawah → kanan, posisi kedua bentuk bergeser berlawanan jarum jam',
  },
];

// ─── 2. BAYANGAN CERMIN ───────────────────────────────────────
export const soalBayangan = [
  {
    id: 'BAY-001',
    jenis: 'bayangan',
    soal: 'Cermin vertikal di tengah. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.triUp(20, 40, 16, '#3B82F6', '#1D4ED8')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(60, 40, 16, '#3B82F6', '#1D4ED8')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triDown(60, 40, 16, '#3B82F6', '#1D4ED8')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triLeft(60, 40, 16, '#3B82F6', '#1D4ED8')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triRight(60, 40, 16, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: posisi kiri-kanan dibalik, atas-bawah tetap. Segitiga atas tetap atas',
  },
  {
    id: 'BAY-002',
    jenis: 'bayangan',
    soal: 'Cermin horizontal di tengah. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.triRight(40, 20, 14, '#10B981', '#047857')
    ),
    pilihan: {
      a: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triLeft(40, 60, 14, '#10B981', '#047857')),
      b: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triRight(40, 60, 14, '#10B981', '#047857')),
      c: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(40, 60, 14, '#10B981', '#047857')),
      d: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triDown(40, 60, 14, '#10B981', '#047857')),
    },
    jawaban: 'b',
    pembahasan: 'Cermin horizontal: atas-bawah dibalik, kiri-kanan tetap. Segitiga kanan tetap kanan',
  },
  {
    id: 'BAY-003',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari huruf "b" adalah?',
    stimulus: svgWrap(shapes.text(25, 40, 'b', 36, '#1D4ED8') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
    pilihan: {
      a: svgWrap(shapes.text(58, 40, 'd', 36, '#1D4ED8') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      b: svgWrap(shapes.text(58, 40, 'p', 36, '#1D4ED8') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      c: svgWrap(shapes.text(58, 40, 'q', 36, '#1D4ED8') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      d: svgWrap(shapes.text(58, 40, 'b', 36, '#1D4ED8') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Bayangan cermin vertikal dari "b" adalah "d" (kiri-kanan terbalik)',
  },
  {
    id: 'BAY-004',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari pola berikut?',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.dot(15, 20, 5, '#EF4444') + shapes.dot(30, 50, 5, '#3B82F6') + shapes.dot(20, 65, 5, '#10B981')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(65, 20, 5, '#EF4444') + shapes.dot(50, 50, 5, '#3B82F6') + shapes.dot(60, 65, 5, '#10B981')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(65, 60, 5, '#EF4444') + shapes.dot(50, 30, 5, '#3B82F6') + shapes.dot(60, 15, 5, '#10B981')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(65, 20, 5, '#10B981') + shapes.dot(50, 50, 5, '#EF4444') + shapes.dot(60, 65, 5, '#3B82F6')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(15, 20, 5, '#EF4444') + shapes.dot(30, 50, 5, '#3B82F6') + shapes.dot(20, 65, 5, '#10B981')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: koordinat x dibalik (x → 80-x), warna dan y tetap sama',
  },
  {
    id: 'BAY-005',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari angka "2" adalah?',
    stimulus: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(22, 40, '2', 36, '#8B5CF6')),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/><text x="58" y="40" text-anchor="middle" dominant-baseline="middle" font-size="36" font-weight="bold" fill="#8B5CF6" transform="scale(-1,1) translate(-80,0)">2</text>`),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, '2', 36, '#8B5CF6')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, 'S', 36, '#8B5CF6')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, 'Z', 36, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Bayangan cermin vertikal angka "2" adalah "2" yang terbalik kiri-kanan',
  },
  {
    id: 'BAY-006',
    jenis: 'bayangan',
    soal: 'Bayangan cermin horizontal dari pola berikut?',
    stimulus: svgWrap(
      `<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.triUp(25, 25, 14, '#F59E0B', '#D97706') + shapes.rect(58, 22, 14, 14, '#EF4444', '#B91C1C')
    ),
    pilihan: {
      a: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triDown(25, 55, 14, '#F59E0B', '#D97706') + shapes.rect(58, 58, 14, 14, '#EF4444', '#B91C1C')),
      b: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(55, 55, 14, '#F59E0B', '#D97706') + shapes.rect(22, 58, 14, 14, '#EF4444', '#B91C1C')),
      c: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triDown(55, 55, 14, '#F59E0B', '#D97706') + shapes.rect(22, 58, 14, 14, '#EF4444', '#B91C1C')),
      d: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(25, 55, 14, '#F59E0B', '#D97706') + shapes.rect(58, 58, 14, 14, '#EF4444', '#B91C1C')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin horizontal: y dibalik, x tetap. Segitiga atas→bawah, posisi x sama',
  },
  {
    id: 'BAY-007',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari kombinasi bentuk berikut?',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.diamond(20, 35, 14, '#8B5CF6', '#6D28D9') + shapes.circle(30, 60, 8, '#F59E0B', '#D97706')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(60, 35, 14, '#8B5CF6', '#6D28D9') + shapes.circle(50, 60, 8, '#F59E0B', '#D97706')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(60, 45, 14, '#8B5CF6', '#6D28D9') + shapes.circle(50, 20, 8, '#F59E0B', '#D97706')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.circle(60, 35, 8, '#8B5CF6', '#6D28D9') + shapes.diamond(50, 60, 14, '#F59E0B', '#D97706')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(20, 35, 14, '#8B5CF6', '#6D28D9') + shapes.circle(30, 60, 8, '#F59E0B', '#D97706')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: x → 80-x. Berlian di x=20 → x=60, lingkaran di x=30 → x=50',
  },
  {
    id: 'BAY-008',
    jenis: 'bayangan',
    soal: 'Cermin vertikal dari kombinasi bentuk berikut. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.triUp(20, 25, 14, '#10B981', '#047857') + shapes.rect(20, 58, 16, 16, '#EF4444', '#B91C1C')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(60, 25, 14, '#10B981', '#047857') + shapes.rect(60, 58, 16, 16, '#EF4444', '#B91C1C')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triDown(60, 25, 14, '#10B981', '#047857') + shapes.rect(60, 58, 16, 16, '#EF4444', '#B91C1C')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(20, 25, 14, '#10B981', '#047857') + shapes.rect(20, 58, 16, 16, '#EF4444', '#B91C1C')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triUp(60, 25, 14, '#EF4444', '#B91C1C') + shapes.rect(60, 58, 16, 16, '#10B981', '#047857')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: hanya posisi x dibalik (x → 80-x). Segitiga atas tetap atas (simetri kiri-kanan)',
  },
  {
    id: 'BAY-009',
    jenis: 'bayangan',
    soal: 'Cermin horizontal dari kombinasi bentuk berikut. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.triRight(25, 20, 14, '#3B82F6', '#1D4ED8') + shapes.dot(55, 58, 6, '#F59E0B')
    ),
    pilihan: {
      a: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triRight(25, 60, 14, '#3B82F6', '#1D4ED8') + shapes.dot(55, 22, 6, '#F59E0B')),
      b: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triLeft(25, 60, 14, '#3B82F6', '#1D4ED8') + shapes.dot(55, 22, 6, '#F59E0B')),
      c: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triRight(25, 20, 14, '#3B82F6', '#1D4ED8') + shapes.dot(55, 58, 6, '#F59E0B')),
      d: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.triRight(25, 60, 14, '#F59E0B', '#D97706') + shapes.dot(55, 22, 6, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin horizontal: hanya posisi y dibalik (y → 80-y). Segitiga kanan tetap kanan (simetri atas-bawah)',
  },
  {
    id: 'BAY-010',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari huruf "p" adalah?',
    stimulus: svgWrap(shapes.text(25, 40, 'p', 36, '#7C3AED') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
    pilihan: {
      a: svgWrap(shapes.text(58, 40, 'q', 36, '#7C3AED') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      b: svgWrap(shapes.text(58, 40, 'b', 36, '#7C3AED') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      c: svgWrap(shapes.text(58, 40, 'd', 36, '#7C3AED') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
      d: svgWrap(shapes.text(58, 40, 'p', 36, '#7C3AED') + `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Bayangan cermin vertikal dari "p" adalah "q" (kiri-kanan terbalik)',
  },
  {
    id: 'BAY-011',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari pola titik berikut?',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.dot(18, 22, 5, '#3B82F6') + shapes.dot(28, 45, 5, '#F59E0B') + shapes.dot(15, 60, 5, '#10B981')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(62, 22, 5, '#3B82F6') + shapes.dot(52, 45, 5, '#F59E0B') + shapes.dot(65, 60, 5, '#10B981')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(62, 60, 5, '#3B82F6') + shapes.dot(52, 35, 5, '#F59E0B') + shapes.dot(65, 20, 5, '#10B981')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(62, 22, 5, '#10B981') + shapes.dot(52, 45, 5, '#3B82F6') + shapes.dot(65, 60, 5, '#F59E0B')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.dot(18, 22, 5, '#3B82F6') + shapes.dot(28, 45, 5, '#F59E0B') + shapes.dot(15, 60, 5, '#10B981')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: koordinat x dibalik (x → 80-x), warna dan y tetap sama',
  },
  {
    id: 'BAY-012',
    jenis: 'bayangan',
    soal: 'Cermin horizontal dari kombinasi bentuk berikut. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.diamond(25, 22, 12, '#8B5CF6', '#6D28D9') + shapes.triDown(58, 25, 10, '#EF4444', '#B91C1C')
    ),
    pilihan: {
      a: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(25, 58, 12, '#8B5CF6', '#6D28D9') + shapes.triUp(58, 55, 10, '#EF4444', '#B91C1C')),
      b: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(25, 58, 12, '#8B5CF6', '#6D28D9') + shapes.triDown(58, 55, 10, '#EF4444', '#B91C1C')),
      c: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(25, 22, 12, '#8B5CF6', '#6D28D9') + shapes.triUp(58, 55, 10, '#EF4444', '#B91C1C')),
      d: svgWrap(`<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.diamond(25, 58, 12, '#EF4444', '#B91C1C') + shapes.triUp(58, 55, 10, '#8B5CF6', '#6D28D9')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin horizontal: y dibalik, x tetap. Belah ketupat tetap sama bentuk, segitiga bawah → atas',
  },
  {
    id: 'BAY-013',
    jenis: 'bayangan',
    soal: 'Bayangan cermin vertikal dari angka "5" adalah?',
    stimulus: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(22, 40, '5', 36, '#059669')),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/><text x="58" y="40" text-anchor="middle" dominant-baseline="middle" font-size="36" font-weight="bold" fill="#059669" transform="scale(-1,1) translate(-116,0)">5</text>`),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, '5', 36, '#059669')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, '2', 36, '#059669')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.text(58, 40, 'S', 36, '#059669')),
    },
    jawaban: 'a',
    pembahasan: 'Bayangan cermin vertikal angka "5" adalah "5" yang terbalik kiri-kanan',
  },
  {
    id: 'BAY-014',
    jenis: 'bayangan',
    soal: 'Cermin vertikal dari kombinasi bentuk berikut. Pilih bayangan yang benar!',
    stimulus: svgWrap(
      `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` +
      shapes.star(20, 30, 12, '#F59E0B', '#D97706') + shapes.rect(28, 58, 14, 14, '#3B82F6', '#1D4ED8')
    ),
    pilihan: {
      a: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.star(60, 30, 12, '#F59E0B', '#D97706') + shapes.rect(52, 58, 14, 14, '#3B82F6', '#1D4ED8')),
      b: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.star(60, 42, 12, '#F59E0B', '#D97706') + shapes.rect(52, 20, 14, 14, '#3B82F6', '#1D4ED8')),
      c: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.star(60, 30, 12, '#3B82F6', '#1D4ED8') + shapes.rect(52, 58, 14, 14, '#F59E0B', '#D97706')),
      d: svgWrap(`<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>` + shapes.star(20, 30, 12, '#F59E0B', '#D97706') + shapes.rect(28, 58, 14, 14, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'a',
    pembahasan: 'Cermin vertikal: hanya x dibalik (x → 80-x), y dan warna tetap sama',
  },
];

// ─── 3. POLA MATRIKS ─────────────────────────────────────────
export const soalMatriks = [
  {
    id: 'MAT-001',
    jenis: 'matriks',
    soal: 'Temukan pola dan lengkapi matriks 3x3 berikut. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      // Grid 3x3
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      // Baris 1: lingkaran kecil, sedang, besar
      `<circle cx="16" cy="16" r="5" fill="#3B82F6"/>` +
      `<circle cx="40" cy="16" r="8" fill="#3B82F6"/>` +
      `<circle cx="64" cy="16" r="11" fill="#3B82F6"/>` +
      // Baris 2: kotak kecil, sedang, besar
      `<rect x="11" y="11" width="10" height="10" fill="#10B981" transform="translate(0,27)"/>` +
      `<rect x="32" y="8" width="16" height="16" fill="#10B981" transform="translate(0,27)"/>` +
      `<rect x="53" y="5" width="22" height="22" fill="#10B981" transform="translate(0,27)"/>` +
      // Baris 3: segitiga kecil, sedang, ?
      shapes.triUp(16, 64, 7, '#EF4444', '#B91C1C') +
      shapes.triUp(40, 64, 11, '#EF4444', '#B91C1C') +
      shapes.text(64, 64, '?', 16, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 15, '#EF4444', '#B91C1C')),
      b: svgWrap(shapes.triUp(40, 40, 11, '#EF4444', '#B91C1C')),
      c: svgWrap(shapes.triDown(40, 40, 15, '#EF4444', '#B91C1C')),
      d: svgWrap(shapes.triUp(40, 40, 15, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris ukuran bertambah (kecil→sedang→besar). Baris 3 segitiga merah, ukuran besar',
  },
  {
    id: 'MAT-002',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.dot(16, 16, 4, '#374151') +
      `<text x="40" y="16" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">••</text>` +
      `<text x="64" y="16" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">•••</text>` +
      `<text x="16" y="40" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">••</text>` +
      `<text x="40" y="40" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">•••</text>` +
      `<text x="64" y="40" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">••••</text>` +
      `<text x="16" y="64" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">•••</text>` +
      `<text x="40" y="64" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="#374151">••••</text>` +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<text x="40" y="40" text-anchor="middle" dominant-baseline="middle" font-size="14" fill="#374151">•••••</text>`),
      b: svgWrap(`<text x="40" y="40" text-anchor="middle" dominant-baseline="middle" font-size="14" fill="#374151">••••</text>`),
      c: svgWrap(`<text x="40" y="40" text-anchor="middle" dominant-baseline="middle" font-size="14" fill="#374151">••</text>`),
      d: svgWrap(`<text x="40" y="40" text-anchor="middle" dominant-baseline="middle" font-size="14" fill="#374151">•••</text>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola: baris+kolom = jumlah titik. Posisi (3,3) = 3+2 = 5 titik',
  },
  {
    id: 'MAT-003',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 18, 18, '#3B82F6') + shapes.rect(40, 16, 18, 18, '#10B981') + shapes.rect(64, 16, 18, 18, '#EF4444') +
      shapes.rect(16, 40, 18, 18, '#10B981') + shapes.rect(40, 40, 18, 18, '#EF4444') + shapes.rect(64, 40, 18, 18, '#3B82F6') +
      shapes.rect(16, 64, 18, 18, '#EF4444') + shapes.rect(40, 64, 18, 18, '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 30, 30, '#10B981')),
      b: svgWrap(shapes.rect(40, 40, 30, 30, '#3B82F6')),
      c: svgWrap(shapes.rect(40, 40, 30, 30, '#EF4444')),
      d: svgWrap(shapes.rect(40, 40, 30, 30, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda (Biru, Hijau, Merah). Posisi (3,3) = Hijau',
  },
  {
    id: 'MAT-004',
    jenis: 'matriks',
    soal: 'Temukan pola rotasi dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#8B5CF6') + shapes.triRight(40, 16, 10, '#8B5CF6') + shapes.triDown(64, 16, 10, '#8B5CF6') +
      shapes.triRight(16, 40, 10, '#8B5CF6') + shapes.triDown(40, 40, 10, '#8B5CF6') + shapes.triLeft(64, 40, 10, '#8B5CF6') +
      shapes.triDown(16, 64, 10, '#8B5CF6') + shapes.triLeft(40, 64, 10, '#8B5CF6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 18, '#8B5CF6')),
      b: svgWrap(shapes.triRight(40, 40, 18, '#8B5CF6')),
      c: svgWrap(shapes.triDown(40, 40, 18, '#8B5CF6')),
      d: svgWrap(shapes.triLeft(40, 40, 18, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap sel rotasi 90° CW dari sel sebelumnya. Setelah Kiri → Atas',
  },
  {
    id: 'MAT-005',
    jenis: 'matriks',
    soal: 'Pola jumlah sisi bentuk bertambah. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      // Segitiga (3 sisi)
      shapes.triUp(16, 16, 9, '#F59E0B') +
      // Kotak (4 sisi)
      shapes.rect(40, 16, 16, 16, '#F59E0B') +
      // Pentagon (5 sisi) — approximate
      `<polygon points="64,7 71,13 69,21 59,21 57,13" fill="#F59E0B" stroke="#D97706" stroke-width="1.5"/>` +
      shapes.triUp(16, 40, 9, '#3B82F6') + shapes.rect(40, 40, 16, 16, '#3B82F6') +
      `<polygon points="64,31 71,37 69,45 59,45 57,37" fill="#3B82F6" stroke="#1D4ED8" stroke-width="1.5"/>` +
      shapes.triUp(16, 64, 9, '#EF4444') + shapes.rect(40, 64, 16, 16, '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<polygon points="40,22 48,28 45,37 35,37 32,28" fill="#EF4444" stroke="#B91C1C" stroke-width="1.5"/>`),
      b: svgWrap(shapes.circle(40, 40, 15, '#EF4444')),
      c: svgWrap(shapes.rect(40, 40, 24, 24, '#EF4444')),
      d: svgWrap(shapes.diamond(40, 40, 16, '#EF4444')),
    },
    jawaban: 'a',
    pembahasan: 'Pola kolom: segitiga→kotak→pentagon. Baris 3 warna merah, kolom 3 = pentagon merah',
  },
  {
    id: 'MAT-006',
    jenis: 'matriks',
    soal: 'Pola: bentuk berputar dan warna bergantian. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#3B82F6') + shapes.triRight(40, 16, 10, '#EF4444') + shapes.triDown(64, 16, 10, '#3B82F6') +
      shapes.triRight(16, 40, 10, '#EF4444') + shapes.triDown(40, 40, 10, '#3B82F6') + shapes.triLeft(64, 40, 10, '#EF4444') +
      shapes.triDown(16, 64, 10, '#3B82F6') + shapes.triLeft(40, 64, 10, '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 18, '#3B82F6')),
      b: svgWrap(shapes.triUp(40, 40, 18, '#EF4444')),
      c: svgWrap(shapes.triRight(40, 40, 18, '#3B82F6')),
      d: svgWrap(shapes.triLeft(40, 40, 18, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola rotasi 90° CW + warna bergantian biru-merah. Posisi (3,3): setelah kiri+merah → atas+biru',
  },
  {
    id: 'MAT-007',
    jenis: 'matriks',
    soal: 'Jumlah bentuk bertambah tiap baris. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.dot(16, 16, 4, '#374151') +
      shapes.dot(37, 16, 4, '#374151') + shapes.dot(44, 16, 4, '#374151') +
      shapes.dot(61, 16, 4, '#374151') + shapes.dot(64, 16, 4, '#374151') + shapes.dot(67, 16, 4, '#374151') +
      shapes.dot(16, 40, 4, '#374151') +
      shapes.dot(37, 40, 4, '#374151') + shapes.dot(44, 40, 4, '#374151') +
      shapes.dot(61, 40, 4, '#374151') + shapes.dot(64, 40, 4, '#374151') + shapes.dot(67, 40, 4, '#374151') +
      shapes.dot(16, 64, 4, '#374151') +
      shapes.dot(37, 64, 4, '#374151') + shapes.dot(44, 64, 4, '#374151') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.dot(35, 40, 5) + shapes.dot(42, 40, 5) + shapes.dot(49, 40, 5)),
      b: svgWrap(shapes.dot(38, 40, 5) + shapes.dot(45, 40, 5)),
      c: svgWrap(shapes.dot(40, 40, 5)),
      d: svgWrap(shapes.dot(33, 40, 5) + shapes.dot(40, 40, 5) + shapes.dot(47, 40, 5) + shapes.dot(54, 40, 5)),
    },
    jawaban: 'a',
    pembahasan: 'Pola kolom: 1→2→3 titik. Kolom 3 baris 3 = 3 titik',
  },
  {
    id: 'MAT-008',
    jenis: 'matriks',
    soal: 'Pola: diagonal utama sama. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 9, '#3B82F6') + shapes.rect(40, 16, 16, 16, '#10B981') + shapes.triUp(64, 16, 9, '#EF4444') +
      shapes.rect(16, 40, 16, 16, '#10B981') + shapes.triUp(40, 40, 9, '#EF4444') + shapes.circle(64, 40, 9, '#3B82F6') +
      shapes.triUp(16, 64, 9, '#EF4444') + shapes.circle(40, 64, 9, '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 26, 26, '#10B981')),
      b: svgWrap(shapes.circle(40, 40, 13, '#3B82F6')),
      c: svgWrap(shapes.triUp(40, 40, 13, '#EF4444')),
      d: svgWrap(shapes.diamond(40, 40, 13, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 bentuk berbeda. (3,3) yang belum ada = Kotak Hijau',
  },
  {
    id: 'MAT-009',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 18, 18, '#F59E0B') + shapes.rect(40, 16, 18, 18, '#14B8A6') + shapes.rect(64, 16, 18, 18, '#EC4899') +
      shapes.rect(16, 40, 18, 18, '#14B8A6') + shapes.rect(40, 40, 18, 18, '#EC4899') + shapes.rect(64, 40, 18, 18, '#F59E0B') +
      shapes.rect(16, 64, 18, 18, '#EC4899') + shapes.rect(40, 64, 18, 18, '#F59E0B') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 30, 30, '#14B8A6')),
      b: svgWrap(shapes.rect(40, 40, 30, 30, '#F59E0B')),
      c: svgWrap(shapes.rect(40, 40, 30, 30, '#EC4899')),
      d: svgWrap(shapes.rect(40, 40, 30, 30, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda (Oranye, Teal, Pink), bergeser satu tiap baris. (3,3) = Teal',
  },
  {
    id: 'MAT-010',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 10, 10, '#3B82F6') + shapes.rect(40, 16, 16, 16, '#3B82F6') + shapes.rect(64, 16, 22, 22, '#3B82F6') +
      shapes.diamond(16, 40, 7, '#10B981') + shapes.diamond(40, 40, 11, '#10B981') + shapes.diamond(64, 40, 15, '#10B981') +
      shapes.star(16, 64, 7, '#EF4444') + shapes.star(40, 64, 11, '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.star(40, 40, 15, '#EF4444')),
      b: svgWrap(shapes.star(40, 40, 11, '#EF4444')),
      c: svgWrap(shapes.circle(40, 40, 15, '#EF4444')),
      d: svgWrap(shapes.star(40, 40, 15, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris ukuran bentuk bertambah besar. Baris 3 bintang merah ukuran besar',
  },
  {
    id: 'MAT-011',
    jenis: 'matriks',
    soal: 'Pola jumlah sisi bentuk bertambah tiap kolom. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 8, '#3B82F6') + shapes.triUp(40, 16, 9, '#3B82F6') + shapes.rect(64, 16, 16, 16, '#3B82F6') +
      shapes.circle(16, 40, 8, '#10B981') + shapes.triUp(40, 40, 9, '#10B981') + shapes.rect(64, 40, 16, 16, '#10B981') +
      shapes.circle(16, 64, 8, '#EF4444') + shapes.triUp(40, 64, 9, '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 24, 24, '#EF4444')),
      b: svgWrap(shapes.circle(40, 40, 14, '#EF4444')),
      c: svgWrap(shapes.triUp(40, 40, 14, '#EF4444')),
      d: svgWrap(shapes.rect(40, 40, 24, 24, '#10B981')),
    },
    jawaban: 'a',
    pembahasan: 'Pola kolom: lingkaran (0 sisi) → segitiga (3 sisi) → kotak (4 sisi). Baris 3 warna merah, kolom 3 = kotak merah',
  },
  {
    id: 'MAT-012',
    jenis: 'matriks',
    soal: 'Temukan pola angka dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.text(16, 16, '2', 18, '#374151') + shapes.text(40, 16, '3', 18, '#374151') + shapes.text(64, 16, '4', 18, '#374151') +
      shapes.text(16, 40, '3', 18, '#374151') + shapes.text(40, 40, '4', 18, '#374151') + shapes.text(64, 40, '5', 18, '#374151') +
      shapes.text(16, 64, '4', 18, '#374151') + shapes.text(40, 64, '5', 18, '#374151') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.text(40, 40, '6', 20, '#374151')),
      b: svgWrap(shapes.text(40, 40, '5', 20, '#374151')),
      c: svgWrap(shapes.text(40, 40, '7', 20, '#374151')),
      d: svgWrap(shapes.text(40, 40, '4', 20, '#374151')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: nilai sel = nomor baris + nomor kolom. Sel (3,3) = 3 + 3 = 6',
  },
  {
    id: 'MAT-013',
    jenis: 'matriks',
    soal: 'Pola rotasi berkelanjutan. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#0EA5E9') + shapes.triRight(40, 16, 10, '#0EA5E9') + shapes.triDown(64, 16, 10, '#0EA5E9') +
      shapes.triLeft(16, 40, 10, '#0EA5E9') + shapes.triUp(40, 40, 10, '#0EA5E9') + shapes.triRight(64, 40, 10, '#0EA5E9') +
      shapes.triDown(16, 64, 10, '#0EA5E9') + shapes.triLeft(40, 64, 10, '#0EA5E9') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 18, '#0EA5E9')),
      b: svgWrap(shapes.triRight(40, 40, 18, '#0EA5E9')),
      c: svgWrap(shapes.triDown(40, 40, 18, '#0EA5E9')),
      d: svgWrap(shapes.triLeft(40, 40, 18, '#0EA5E9')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap sel berputar 90° CW berkelanjutan mengikuti urutan baca. Setelah Kiri → Atas',
  },
  {
    id: 'MAT-014',
    jenis: 'matriks',
    soal: 'Pola isi/kosong menyerupai papan catur. Pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      `<circle cx="16" cy="16" r="9" fill="#10B981" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="40" cy="16" r="9" fill="none" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="64" cy="16" r="9" fill="#10B981" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="16" cy="40" r="9" fill="none" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="40" cy="40" r="9" fill="#10B981" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="64" cy="40" r="9" fill="none" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="16" cy="64" r="9" fill="#10B981" stroke="#047857" stroke-width="1.5"/>` +
      `<circle cx="40" cy="64" r="9" fill="none" stroke="#047857" stroke-width="1.5"/>` +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<circle cx="40" cy="40" r="15" fill="#10B981" stroke="#047857" stroke-width="2"/>`),
      b: svgWrap(`<circle cx="40" cy="40" r="15" fill="none" stroke="#047857" stroke-width="2"/>`),
      c: svgWrap(shapes.rect(40, 40, 24, 24, '#10B981')),
      d: svgWrap(`<circle cx="40" cy="40" r="15" fill="#EF4444" stroke="#B91C1C" stroke-width="2"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola papan catur: sel terisi jika (baris+kolom) genap. Sel (3,3): 3+3=6 (genap) → terisi',
  },
  {
    id: 'MAT-015',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk & warna, pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 9, '#3B82F6') + shapes.star(40, 16, 9, '#10B981') + shapes.plus(64, 16, 9, '#EF4444') +
      shapes.star(16, 40, 9, '#10B981') + shapes.plus(40, 40, 9, '#EF4444') + shapes.circle(64, 40, 9, '#3B82F6') +
      shapes.plus(16, 64, 9, '#EF4444') + shapes.circle(40, 64, 9, '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.star(40, 40, 15, '#10B981')),
      b: svgWrap(shapes.circle(40, 40, 15, '#3B82F6')),
      c: svgWrap(shapes.plus(40, 40, 15, '#EF4444')),
      d: svgWrap(shapes.diamond(40, 40, 15, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 bentuk berbeda (lingkaran biru, bintang hijau, plus merah). (3,3) yang belum ada = Bintang Hijau',
  },

  // --- Dari file MINI ATUR PSY.ppt (Ojik) — bagian "Pola Matriks" ---
  // File aslinya berupa gambar (bukan kode), semua pola direkonstruksi ulang
  // jadi SVG dan sudah dicek visual sebelum dimasukkan (render preview
  // dicocokkan ke gambar aslinya). 1 soal (hook/kait melengkung) DILEWATI
  // karena bentuknya terlalu rumit untuk digambar ulang akurat pakai
  // shape builder yang ada — lihat catatan di chat.
  {
    id: 'MAT-016',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#3B82F6') + shapes.circle(40, 16, 10, 'none', '#10B981') + shapes.plus(64, 16, 9) +
      shapes.circle(16, 40, 10, 'none', '#10B981') + shapes.plus(40, 40, 9) + shapes.rect(64, 40, 16, 16, 'none', '#3B82F6') +
      shapes.plus(16, 64, 9) + shapes.rect(40, 64, 16, 16, 'none', '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 14, 'none', '#10B981')),
      b: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#3B82F6')),
      c: svgWrap(shapes.plus(40, 40, 15)),
      d: svgWrap(shapes.diamond(40, 40, 15, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola kotak-ajaib: tiap baris & kolom berisi Kotak, Lingkaran, dan Plus masing-masing sekali. Baris 3 & kolom 3 yang belum ada = Lingkaran',
  },
  {
    id: 'MAT-017',
    jenis: 'matriks',
    soal: 'Temukan pola hiasan pada kotak dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#374151') +
      shapes.rect(40, 16, 16, 16, 'none', '#374151') + shapes.plus(40, 7, 4) +
      shapes.rect(64, 16, 16, 16, 'none', '#374151') + shapes.dot(64, 16) +
      shapes.rect(16, 40, 16, 16, 'none', '#374151') + shapes.dot(16, 40) +
      shapes.rect(40, 40, 16, 16, 'none', '#374151') +
      shapes.rect(64, 40, 16, 16, 'none', '#374151') + shapes.plus(64, 31, 4) +
      shapes.rect(16, 64, 16, 16, 'none', '#374151') + shapes.plus(16, 55, 4) +
      shapes.rect(40, 64, 16, 16, 'none', '#374151') + shapes.dot(40, 64) +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151')),
      b: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151') + shapes.dot(40, 40)),
      c: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151') + shapes.plus(40, 28, 5)),
      d: svgWrap(shapes.circle(40, 40, 14, 'none', '#374151') + shapes.rect(40, 40, 24, 24, 'none', '#374151')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi kotak Polos, kotak+Plus, dan kotak+Titik masing-masing sekali. Baris 3 & kolom 3 yang belum ada = kotak Polos',
  },
  {
    id: 'MAT-018',
    jenis: 'matriks',
    soal: 'Temukan pola kotak bersilang dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#374151') +
      shapes.rect(40, 16, 16, 16, 'none', '#374151') + shapes.diagLine(33, 9, 47, 23) + shapes.diagLine(47, 9, 33, 23) +
      shapes.rect(64, 16, 16, 16, 'none', '#374151') +
      shapes.rect(16, 40, 16, 16, 'none', '#374151') + shapes.diagLine(9, 33, 23, 47) + shapes.diagLine(23, 33, 9, 47) +
      shapes.rect(40, 40, 16, 16, 'none', '#374151') +
      shapes.rect(64, 40, 16, 16, 'none', '#374151') +
      shapes.rect(16, 64, 16, 16, 'none', '#374151') +
      shapes.rect(40, 64, 16, 16, 'none', '#374151') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151')),
      b: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151') + shapes.diagLine(28, 28, 52, 52) + shapes.diagLine(52, 28, 28, 52)),
      c: svgWrap(shapes.rect(40, 40, 24, 24, 'none', '#374151') + shapes.diagLine(28, 28, 52, 52)),
      d: svgWrap(`<line x1="27" y1="40" x2="53" y2="40" stroke="#374151" stroke-width="2"/><line x1="40" y1="27" x2="40" y2="53" stroke="#374151" stroke-width="2"/>` + shapes.rect(40, 40, 24, 24, 'none', '#374151')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: kotak bersilang (X) muncul tepat 1 kali di tiap baris & kolom. Baris 3 & kolom 3 belum ada kotak silang, jadi "?" = kotak dengan tanda X',
  },
  {
    id: 'MAT-019',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      `<polygon points="16,7 23,11 23,21 16,25 9,21 9,11" fill="none" stroke="#F59E0B" stroke-width="1.5"/>` +
      shapes.rect(40, 16, 14, 14, 'none', '#F59E0B') + shapes.triUp(64, 16, 9, 'none', '#F59E0B') +
      shapes.rect(16, 40, 14, 14, 'none', '#F59E0B') + shapes.triUp(40, 40, 9, 'none', '#F59E0B') +
      `<polygon points="64,31 71,35 71,45 64,49 57,45 57,35" fill="none" stroke="#F59E0B" stroke-width="1.5"/>` +
      shapes.triUp(16, 64, 9, 'none', '#F59E0B') +
      `<polygon points="40,55 47,59 47,69 40,73 33,69 33,59" fill="none" stroke="#F59E0B" stroke-width="1.5"/>` +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#F59E0B')),
      b: svgWrap(shapes.triUp(40, 40, 16, 'none', '#F59E0B')),
      c: svgWrap(`<polygon points="40,22 48,27 48,38 40,43 32,38 32,27" fill="none" stroke="#F59E0B" stroke-width="1.5"/>`),
      d: svgWrap(shapes.diamond(40, 40, 16, 'none', '#F59E0B')),
    },
    jawaban: 'a',
    pembahasan: 'Pola kotak-ajaib bentuk: tiap baris & kolom berisi Segi Enam, Kotak, dan Segitiga masing-masing sekali. Baris 3 & kolom 3 yang belum ada = Kotak',
  },
  {
    id: 'MAT-020',
    jenis: 'matriks',
    soal: 'Temukan pola garis arsiran dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.diagLine(9, 23, 15, 9) + shapes.diagLine(16, 23, 22, 9) + shapes.diagLine(23, 23, 29, 9) +
      shapes.diagLine(33, 8, 33, 24) + shapes.diagLine(40, 8, 40, 24) + shapes.diagLine(47, 8, 47, 24) +
      shapes.diagLine(57, 12, 71, 12) + shapes.diagLine(57, 16, 71, 16) + shapes.diagLine(57, 20, 71, 20) +
      shapes.diagLine(9, 33, 9, 47) + shapes.diagLine(16, 33, 16, 47) +
      shapes.diagLine(33, 36, 47, 36) + shapes.diagLine(33, 40, 47, 40) + shapes.diagLine(33, 44, 47, 44) +
      shapes.diagLine(57, 47, 63, 33) + shapes.diagLine(64, 47, 70, 33) +
      shapes.diagLine(9, 60, 23, 60) + shapes.diagLine(9, 64, 23, 64) + shapes.diagLine(9, 68, 23, 68) +
      shapes.diagLine(33, 71, 39, 57) + shapes.diagLine(40, 71, 46, 57) + shapes.diagLine(47, 71, 53, 57) +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diagLine(30, 51, 36, 37) + shapes.diagLine(37, 51, 43, 37) + shapes.diagLine(44, 51, 50, 37)),
      b: svgWrap(shapes.diagLine(30, 37, 30, 51) + shapes.diagLine(37, 37, 37, 51) + shapes.diagLine(44, 37, 44, 51)),
      c: svgWrap(shapes.diagLine(28, 40, 52, 40) + shapes.diagLine(28, 44, 52, 44) + shapes.diagLine(28, 48, 52, 48)),
      d: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#374151')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: tiap baris & kolom berisi arsiran Diagonal, Vertikal, dan Horizontal masing-masing sekali. Baris 3 & kolom 3 yang belum ada = arsiran Vertikal',
  },

  // --- Dari MINI ATUR PSY.ppt — batch 2 (slide 2) ---
  {
    id: 'MAT-021',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#3B82F6') + shapes.triUp(40, 16, 9, 'none', '#10B981') + shapes.circle(64, 16, 10, 'none', '#EF4444') +
      shapes.rect(16, 40, 16, 16, 'none', '#3B82F6') + shapes.triUp(40, 40, 9, 'none', '#10B981') + shapes.circle(64, 40, 10, 'none', '#EF4444') +
      shapes.rect(16, 64, 16, 16, 'none', '#3B82F6') + shapes.triUp(40, 64, 9, 'none', '#10B981') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 14, 'none', '#EF4444')),
      b: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#3B82F6')),
      c: svgWrap(shapes.triUp(40, 40, 16, 'none', '#10B981')),
      d: svgWrap(`<polygon points="40,25 50,32 50,48 30,48 30,32" fill="none" stroke="#F59E0B" stroke-width="2"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap kolom selalu bentuk yang sama (kolom 1=Kotak, kolom 2=Segitiga, kolom 3=Lingkaran) di semua baris. Kolom 3 = Lingkaran',
  },
  {
    id: 'MAT-022',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk & hiasan, pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#3B82F6') +
      shapes.rect(40, 16, 16, 16, 'none', '#3B82F6') + shapes.plus(40, 5, 4) +
      shapes.rect(64, 16, 16, 16, 'none', '#3B82F6') + shapes.dot(64, 16) +
      shapes.triUp(16, 40, 8, 'none', '#10B981') + `<circle cx="16" cy="41" r="11" fill="none" stroke="#EF4444" stroke-width="1.5"/>` +
      shapes.triUp(40, 40, 9, 'none', '#10B981') +
      shapes.triUp(64, 40, 9, 'none', '#10B981') + shapes.plus(64, 27, 4) +
      `<polygon points="16,55 24,62 24,73 8,73 8,62" fill="none" stroke="#F59E0B" stroke-width="2"/>` + shapes.plus(16, 51, 4) +
      `<polygon points="40,55 48,62 48,73 32,73 32,62" fill="none" stroke="#F59E0B" stroke-width="2"/>` + shapes.dot(40, 68) +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<polygon points="40,25 50,32 50,48 30,48 30,32" fill="none" stroke="#F59E0B" stroke-width="2"/>`),
      b: svgWrap(shapes.triUp(40, 40, 16, 'none', '#10B981') + shapes.plus(40, 24, 5)),
      c: svgWrap(`<polygon points="40,25 50,32 50,48 30,48 30,32" fill="none" stroke="#F59E0B" stroke-width="2"/>` + shapes.plus(40, 22, 5)),
      d: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: baris 1=Kotak, baris 2=Segitiga, baris 3=Rumah, dan hiasannya bergeser tiap baris (Polos→Plus→Titik, lalu Titik→Polos→Plus, dst). Kolom 3 baris 3 = Rumah polos',
  },
  {
    id: 'MAT-023',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#3B82F6') + shapes.triUp(40, 16, 9, 'none', '#10B981') + shapes.circle(64, 16, 10, 'none', '#EF4444') +
      shapes.triUp(16, 40, 9, 'none', '#10B981') + shapes.circle(40, 40, 10, 'none', '#EF4444') + shapes.rect(64, 40, 16, 16, 'none', '#3B82F6') +
      shapes.circle(16, 64, 10, 'none', '#EF4444') + shapes.rect(40, 64, 16, 16, 'none', '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 16, 'none', '#10B981')),
      b: svgWrap(shapes.circle(40, 40, 14, 'none', '#EF4444')),
      c: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#3B82F6')),
      d: svgWrap(`<polygon points="40,25 50,32 50,48 30,48 30,32" fill="none" stroke="#F59E0B" stroke-width="2"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola kotak-ajaib: tiap baris & kolom berisi Kotak, Segitiga, dan Lingkaran masing-masing sekali. Baris 3 & kolom 3 yang belum ada = Segitiga',
  },
  {
    id: 'MAT-024',
    jenis: 'matriks',
    soal: 'Temukan pola bentuk dan pilih yang mengisi "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, 'none', '#3B82F6') + shapes.triUp(40, 16, 9, 'none', '#10B981') + shapes.circle(64, 16, 10, 'none', '#EF4444') +
      shapes.circle(16, 40, 10, 'none', '#EF4444') + shapes.rect(40, 40, 16, 16, 'none', '#3B82F6') + shapes.triUp(64, 40, 9, 'none', '#10B981') +
      shapes.triUp(16, 64, 9, 'none', '#10B981') + shapes.circle(40, 64, 10, 'none', '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 22, 22, 'none', '#3B82F6')),
      b: svgWrap(shapes.triUp(40, 40, 16, 'none', '#10B981')),
      c: svgWrap(shapes.circle(40, 40, 14, 'none', '#EF4444')),
      d: svgWrap(`<polygon points="40,25 50,32 50,48 30,48 30,32" fill="none" stroke="#F59E0B" stroke-width="2"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola kotak-ajaib: tiap baris & kolom berisi Kotak, Segitiga, dan Lingkaran masing-masing sekali. Baris 3 & kolom 3 yang belum ada = Kotak',
  },
  // ─── MAT-025 s/d MAT-045: soal orisinal buatan sendiri (bukan dari ───
  // sumber PPT) — dibuat untuk menutup soal yang di-drop karena terlalu
  // berisiko direkonstruksi dari gambar. Semua dibangun & diverifikasi
  // lewat render SVG->PNG sebelum masuk sini, jadi jawaban dijamin benar
  // by construction (latin-square / pola ukuran yang valid secara matematis).
  {
    id: 'MAT-025',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 9, '#3B82F6', '#1D4ED8') +
      shapes.rect(40, 16, 16, 16, '#EF4444', '#B91C1C') +
      shapes.triUp(64, 16, 10, '#F59E0B', '#D97706') +
      shapes.rect(16, 40, 16, 16, '#EF4444', '#B91C1C') +
      shapes.triUp(40, 40, 10, '#F59E0B', '#D97706') +
      shapes.circle(64, 40, 9, '#3B82F6', '#1D4ED8') +
      shapes.triUp(16, 64, 10, '#F59E0B', '#D97706') +
      shapes.circle(40, 64, 9, '#3B82F6', '#1D4ED8') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 16, 16, '#EF4444', '#B91C1C')),
      b: svgWrap(shapes.circle(40, 40, 9, '#3B82F6', '#1D4ED8')),
      c: svgWrap(shapes.triUp(40, 40, 10, '#F59E0B', '#D97706')),
      d: svgWrap(shapes.rect(40, 40, 16, 16, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk lingkaran/kotak/segitiga masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = kotak warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-026',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.diamond(16, 16, 10, '#10B981', '#047857') +
      shapes.star(40, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.plus(64, 16, 13, '#14B8A6', '#0F766E') +
      shapes.star(16, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.plus(40, 40, 13, '#14B8A6', '#0F766E') +
      shapes.diamond(64, 40, 10, '#10B981', '#047857') +
      shapes.plus(16, 64, 13, '#14B8A6', '#0F766E') +
      shapes.diamond(40, 64, 10, '#10B981', '#047857') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diamond(40, 40, 10, '#10B981', '#047857')),
      b: svgWrap(shapes.star(40, 40, 10, '#8B5CF6', '#6D28D9')),
      c: svgWrap(shapes.star(40, 40, 10, '#10B981', '#047857')),
      d: svgWrap(shapes.plus(40, 40, 13, '#14B8A6', '#0F766E')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk belah ketupat/bintang/tanda plus masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = bintang warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-027',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 9, '#EF4444', '#B91C1C') +
      shapes.diamond(40, 16, 10, '#F59E0B', '#D97706') +
      shapes.triUp(64, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.diamond(16, 40, 10, '#F59E0B', '#D97706') +
      shapes.triUp(40, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.circle(64, 40, 9, '#EF4444', '#B91C1C') +
      shapes.triUp(16, 64, 10, '#3B82F6', '#1D4ED8') +
      shapes.circle(40, 64, 9, '#EF4444', '#B91C1C') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 10, '#3B82F6', '#1D4ED8')),
      b: svgWrap(shapes.diamond(40, 40, 10, '#EF4444', '#B91C1C')),
      c: svgWrap(shapes.diamond(40, 40, 10, '#F59E0B', '#D97706')),
      d: svgWrap(shapes.circle(40, 40, 9, '#EF4444', '#B91C1C')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk lingkaran/belah ketupat/segitiga masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = belah ketupat warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-028',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, '#8B5CF6', '#6D28D9') +
      shapes.star(40, 16, 10, '#14B8A6', '#0F766E') +
      shapes.circle(64, 16, 9, '#10B981', '#047857') +
      shapes.star(16, 40, 10, '#14B8A6', '#0F766E') +
      shapes.circle(40, 40, 9, '#10B981', '#047857') +
      shapes.rect(64, 40, 16, 16, '#8B5CF6', '#6D28D9') +
      shapes.circle(16, 64, 9, '#10B981', '#047857') +
      shapes.rect(40, 64, 16, 16, '#8B5CF6', '#6D28D9') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.star(40, 40, 10, '#8B5CF6', '#6D28D9')),
      b: svgWrap(shapes.circle(40, 40, 9, '#10B981', '#047857')),
      c: svgWrap(shapes.rect(40, 40, 16, 16, '#8B5CF6', '#6D28D9')),
      d: svgWrap(shapes.star(40, 40, 10, '#14B8A6', '#0F766E')),
    },
    jawaban: 'd',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk kotak/bintang/lingkaran masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = bintang warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-029',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#F59E0B', '#D97706') +
      shapes.plus(40, 16, 13, '#3B82F6', '#1D4ED8') +
      shapes.diamond(64, 16, 10, '#EF4444', '#B91C1C') +
      shapes.plus(16, 40, 13, '#3B82F6', '#1D4ED8') +
      shapes.diamond(40, 40, 10, '#EF4444', '#B91C1C') +
      shapes.triUp(64, 40, 10, '#F59E0B', '#D97706') +
      shapes.diamond(16, 64, 10, '#EF4444', '#B91C1C') +
      shapes.triUp(40, 64, 10, '#F59E0B', '#D97706') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 10, '#F59E0B', '#D97706')),
      b: svgWrap(shapes.plus(40, 40, 13, '#F59E0B', '#D97706')),
      c: svgWrap(shapes.plus(40, 40, 13, '#3B82F6', '#1D4ED8')),
      d: svgWrap(shapes.diamond(40, 40, 10, '#EF4444', '#B91C1C')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk segitiga/tanda plus/belah ketupat masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = tanda plus warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-030',
    jenis: 'matriks',
    soal: 'Temukan pola dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.star(16, 16, 10, '#14B8A6', '#0F766E') +
      shapes.rect(40, 16, 16, 16, '#10B981', '#047857') +
      shapes.plus(64, 16, 13, '#8B5CF6', '#6D28D9') +
      shapes.rect(16, 40, 16, 16, '#10B981', '#047857') +
      shapes.plus(40, 40, 13, '#8B5CF6', '#6D28D9') +
      shapes.star(64, 40, 10, '#14B8A6', '#0F766E') +
      shapes.plus(16, 64, 13, '#8B5CF6', '#6D28D9') +
      shapes.star(40, 64, 10, '#14B8A6', '#0F766E') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.plus(40, 40, 13, '#8B5CF6', '#6D28D9')),
      b: svgWrap(shapes.rect(40, 40, 16, 16, '#10B981', '#047857')),
      c: svgWrap(shapes.rect(40, 40, 16, 16, '#14B8A6', '#0F766E')),
      d: svgWrap(shapes.star(40, 40, 10, '#14B8A6', '#0F766E')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: setiap baris & kolom berisi bentuk bintang/kotak/tanda plus masing-masing tepat satu kali (latin square). Kotak baris-3 kolom-3 = kotak warna sesuai pola kolomnya.',
  },
  {
    id: 'MAT-031',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.rect(16, 16, 16, 16, '#3B82F6', '#1D4ED8') +
      shapes.rect(40, 16, 16, 16, '#10B981', '#047857') +
      shapes.rect(64, 16, 16, 16, '#EF4444', '#B91C1C') +
      shapes.rect(16, 40, 16, 16, '#10B981', '#047857') +
      shapes.rect(40, 40, 16, 16, '#EF4444', '#B91C1C') +
      shapes.rect(64, 40, 16, 16, '#3B82F6', '#1D4ED8') +
      shapes.rect(16, 64, 16, 16, '#EF4444', '#B91C1C') +
      shapes.rect(40, 64, 16, 16, '#3B82F6', '#1D4ED8') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 23, 23, '#10B981', '#047857')),
      b: svgWrap(shapes.rect(40, 40, 16, 16, '#EF4444', '#B91C1C')),
      c: svgWrap(shapes.rect(40, 40, 16, 16, '#3B82F6', '#1D4ED8')),
      d: svgWrap(shapes.rect(40, 40, 16, 16, '#10B981', '#047857')),
    },
    jawaban: 'd',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-032',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 9, '#10B981', '#047857') +
      shapes.circle(40, 16, 9, '#8B5CF6', '#6D28D9') +
      shapes.circle(64, 16, 9, '#F59E0B', '#D97706') +
      shapes.circle(16, 40, 9, '#8B5CF6', '#6D28D9') +
      shapes.circle(40, 40, 9, '#F59E0B', '#D97706') +
      shapes.circle(64, 40, 9, '#10B981', '#047857') +
      shapes.circle(16, 64, 9, '#F59E0B', '#D97706') +
      shapes.circle(40, 64, 9, '#10B981', '#047857') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 9, '#10B981', '#047857')),
      b: svgWrap(shapes.circle(40, 40, 13, '#8B5CF6', '#6D28D9')),
      c: svgWrap(shapes.circle(40, 40, 9, '#8B5CF6', '#6D28D9')),
      d: svgWrap(shapes.circle(40, 40, 9, '#F59E0B', '#D97706')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-033',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.diamond(16, 16, 10, '#EF4444', '#B91C1C') +
      shapes.diamond(40, 16, 10, '#F59E0B', '#D97706') +
      shapes.diamond(64, 16, 10, '#14B8A6', '#0F766E') +
      shapes.diamond(16, 40, 10, '#F59E0B', '#D97706') +
      shapes.diamond(40, 40, 10, '#14B8A6', '#0F766E') +
      shapes.diamond(64, 40, 10, '#EF4444', '#B91C1C') +
      shapes.diamond(16, 64, 10, '#14B8A6', '#0F766E') +
      shapes.diamond(40, 64, 10, '#EF4444', '#B91C1C') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diamond(40, 40, 10, '#14B8A6', '#0F766E')),
      b: svgWrap(shapes.diamond(40, 40, 10, '#F59E0B', '#D97706')),
      c: svgWrap(shapes.diamond(40, 40, 14, '#F59E0B', '#D97706')),
      d: svgWrap(shapes.diamond(40, 40, 10, '#EF4444', '#B91C1C')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-034',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.triUp(40, 16, 10, '#14B8A6', '#0F766E') +
      shapes.triUp(64, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.triUp(16, 40, 10, '#14B8A6', '#0F766E') +
      shapes.triUp(40, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.triUp(64, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.triUp(16, 64, 10, '#3B82F6', '#1D4ED8') +
      shapes.triUp(40, 64, 10, '#8B5CF6', '#6D28D9') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 14, '#14B8A6', '#0F766E')),
      b: svgWrap(shapes.triUp(40, 40, 10, '#8B5CF6', '#6D28D9')),
      c: svgWrap(shapes.triUp(40, 40, 10, '#14B8A6', '#0F766E')),
      d: svgWrap(shapes.triUp(40, 40, 10, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-035',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.star(16, 16, 10, '#F59E0B', '#D97706') +
      shapes.star(40, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.star(64, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.star(16, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.star(40, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.star(64, 40, 10, '#F59E0B', '#D97706') +
      shapes.star(16, 64, 10, '#8B5CF6', '#6D28D9') +
      shapes.star(40, 64, 10, '#F59E0B', '#D97706') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.star(40, 40, 10, '#3B82F6', '#1D4ED8')),
      b: svgWrap(shapes.star(40, 40, 10, '#8B5CF6', '#6D28D9')),
      c: svgWrap(shapes.star(40, 40, 14, '#3B82F6', '#1D4ED8')),
      d: svgWrap(shapes.star(40, 40, 10, '#F59E0B', '#D97706')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-036',
    jenis: 'matriks',
    soal: 'Temukan pola warna dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.plus(16, 16, 13, '#14B8A6', '#0F766E') +
      shapes.plus(40, 16, 13, '#EF4444', '#B91C1C') +
      shapes.plus(64, 16, 13, '#10B981', '#047857') +
      shapes.plus(16, 40, 13, '#EF4444', '#B91C1C') +
      shapes.plus(40, 40, 13, '#10B981', '#047857') +
      shapes.plus(64, 40, 13, '#14B8A6', '#0F766E') +
      shapes.plus(16, 64, 13, '#10B981', '#047857') +
      shapes.plus(40, 64, 13, '#14B8A6', '#0F766E') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.plus(40, 40, 13, '#EF4444', '#B91C1C')),
      b: svgWrap(shapes.plus(40, 40, 13, '#14B8A6', '#0F766E')),
      c: svgWrap(shapes.plus(40, 40, 13, '#10B981', '#047857')),
      d: svgWrap(shapes.plus(40, 40, 18, '#EF4444', '#B91C1C')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris & kolom berisi 3 warna berbeda pada bentuk yang sama (latin square warna). Kotak baris-3 kolom-3 warnanya mengikuti pola kolom ke-3.',
  },
  {
    id: 'MAT-037',
    jenis: 'matriks',
    soal: 'Temukan pola arah segitiga dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.triRight(40, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.triDown(64, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.triRight(16, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.triDown(40, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.triUp(64, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.triDown(16, 64, 10, '#3B82F6', '#1D4ED8') +
      shapes.triUp(40, 64, 10, '#3B82F6', '#1D4ED8') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triUp(40, 40, 10, '#3B82F6', '#1D4ED8')),
      b: svgWrap(shapes.triRight(40, 40, 10, '#3B82F6', '#1D4ED8')),
      c: svgWrap(shapes.triRight(40, 40, 10, '#94A3B8', '#64748B')),
      d: svgWrap(shapes.triDown(40, 40, 10, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: arah segitiga (atas/kanan/bawah) berputar mengikuti latin square di tiap baris & kolom. Kotak baris-3 kolom-3 mengarah ke kanan.',
  },
  {
    id: 'MAT-038',
    jenis: 'matriks',
    soal: 'Temukan pola arah segitiga dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triRight(16, 16, 10, '#10B981', '#047857') +
      shapes.triDown(40, 16, 10, '#10B981', '#047857') +
      shapes.triLeft(64, 16, 10, '#10B981', '#047857') +
      shapes.triDown(16, 40, 10, '#10B981', '#047857') +
      shapes.triLeft(40, 40, 10, '#10B981', '#047857') +
      shapes.triRight(64, 40, 10, '#10B981', '#047857') +
      shapes.triLeft(16, 64, 10, '#10B981', '#047857') +
      shapes.triRight(40, 64, 10, '#10B981', '#047857') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 10, '#10B981', '#047857')),
      b: svgWrap(shapes.triDown(40, 40, 10, '#94A3B8', '#64748B')),
      c: svgWrap(shapes.triDown(40, 40, 10, '#10B981', '#047857')),
      d: svgWrap(shapes.triRight(40, 40, 10, '#10B981', '#047857')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: arah segitiga (kanan/bawah/kiri) berputar mengikuti latin square di tiap baris & kolom. Kotak baris-3 kolom-3 mengarah ke bawah.',
  },
  {
    id: 'MAT-039',
    jenis: 'matriks',
    soal: 'Temukan pola arah segitiga dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triDown(16, 16, 10, '#EF4444', '#B91C1C') +
      shapes.triLeft(40, 16, 10, '#EF4444', '#B91C1C') +
      shapes.triUp(64, 16, 10, '#EF4444', '#B91C1C') +
      shapes.triLeft(16, 40, 10, '#EF4444', '#B91C1C') +
      shapes.triUp(40, 40, 10, '#EF4444', '#B91C1C') +
      shapes.triDown(64, 40, 10, '#EF4444', '#B91C1C') +
      shapes.triUp(16, 64, 10, '#EF4444', '#B91C1C') +
      shapes.triDown(40, 64, 10, '#EF4444', '#B91C1C') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 10, '#94A3B8', '#64748B')),
      b: svgWrap(shapes.triUp(40, 40, 10, '#EF4444', '#B91C1C')),
      c: svgWrap(shapes.triDown(40, 40, 10, '#EF4444', '#B91C1C')),
      d: svgWrap(shapes.triLeft(40, 40, 10, '#EF4444', '#B91C1C')),
    },
    jawaban: 'd',
    pembahasan: 'Pola: arah segitiga (bawah/kiri/atas) berputar mengikuti latin square di tiap baris & kolom. Kotak baris-3 kolom-3 mengarah ke kiri.',
  },
  {
    id: 'MAT-040',
    jenis: 'matriks',
    soal: 'Temukan pola arah segitiga dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triLeft(16, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.triUp(40, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.triRight(64, 16, 10, '#8B5CF6', '#6D28D9') +
      shapes.triUp(16, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.triRight(40, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.triLeft(64, 40, 10, '#8B5CF6', '#6D28D9') +
      shapes.triRight(16, 64, 10, '#8B5CF6', '#6D28D9') +
      shapes.triLeft(40, 64, 10, '#8B5CF6', '#6D28D9') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 10, '#8B5CF6', '#6D28D9')),
      b: svgWrap(shapes.triUp(40, 40, 10, '#94A3B8', '#64748B')),
      c: svgWrap(shapes.triUp(40, 40, 10, '#8B5CF6', '#6D28D9')),
      d: svgWrap(shapes.triRight(40, 40, 10, '#8B5CF6', '#6D28D9')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: arah segitiga (kiri/atas/kanan) berputar mengikuti latin square di tiap baris & kolom. Kotak baris-3 kolom-3 mengarah ke atas.',
  },
  {
    id: 'MAT-041',
    jenis: 'matriks',
    soal: 'Temukan pola arah segitiga dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.triUp(16, 16, 10, '#F59E0B', '#D97706') +
      shapes.triDown(40, 16, 10, '#F59E0B', '#D97706') +
      shapes.triLeft(64, 16, 10, '#F59E0B', '#D97706') +
      shapes.triDown(16, 40, 10, '#F59E0B', '#D97706') +
      shapes.triLeft(40, 40, 10, '#F59E0B', '#D97706') +
      shapes.triUp(64, 40, 10, '#F59E0B', '#D97706') +
      shapes.triLeft(16, 64, 10, '#F59E0B', '#D97706') +
      shapes.triUp(40, 64, 10, '#F59E0B', '#D97706') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 10, '#F59E0B', '#D97706')),
      b: svgWrap(shapes.triDown(40, 40, 10, '#F59E0B', '#D97706')),
      c: svgWrap(shapes.triDown(40, 40, 10, '#94A3B8', '#64748B')),
      d: svgWrap(shapes.triUp(40, 40, 10, '#F59E0B', '#D97706')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: arah segitiga (atas/bawah/kiri) berputar mengikuti latin square di tiap baris & kolom. Kotak baris-3 kolom-3 mengarah ke bawah.',
  },
  {
    id: 'MAT-042',
    jenis: 'matriks',
    soal: 'Temukan pola ukuran dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.diamond(16, 16, 6, '#3B82F6', '#1D4ED8') +
      shapes.diamond(40, 16, 10, '#3B82F6', '#1D4ED8') +
      shapes.diamond(64, 16, 14, '#3B82F6', '#1D4ED8') +
      shapes.diamond(16, 40, 6, '#3B82F6', '#1D4ED8') +
      shapes.diamond(40, 40, 10, '#3B82F6', '#1D4ED8') +
      shapes.diamond(64, 40, 14, '#3B82F6', '#1D4ED8') +
      shapes.diamond(16, 64, 6, '#3B82F6', '#1D4ED8') +
      shapes.diamond(40, 64, 10, '#3B82F6', '#1D4ED8') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diamond(40, 40, 6, '#3B82F6', '#1D4ED8')),
      b: svgWrap(shapes.diamond(40, 40, 14, '#3B82F6', '#1D4ED8')),
      c: svgWrap(shapes.diamond(40, 40, 14, '#94A3B8', '#64748B')),
      d: svgWrap(shapes.diamond(40, 40, 10, '#3B82F6', '#1D4ED8')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: tiap baris ukuran belah ketupat membesar (kecil→sedang→besar). Baris 3 kolom 3 = ukuran besar.',
  },
  {
    id: 'MAT-043',
    jenis: 'matriks',
    soal: 'Temukan pola ukuran dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.star(16, 16, 6, '#EF4444', '#B91C1C') +
      shapes.star(40, 16, 9, '#EF4444', '#B91C1C') +
      shapes.star(64, 16, 12, '#EF4444', '#B91C1C') +
      shapes.star(16, 40, 6, '#EF4444', '#B91C1C') +
      shapes.star(40, 40, 9, '#EF4444', '#B91C1C') +
      shapes.star(64, 40, 12, '#EF4444', '#B91C1C') +
      shapes.star(16, 64, 6, '#EF4444', '#B91C1C') +
      shapes.star(40, 64, 9, '#EF4444', '#B91C1C') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.star(40, 40, 12, '#94A3B8', '#64748B')),
      b: svgWrap(shapes.star(40, 40, 9, '#EF4444', '#B91C1C')),
      c: svgWrap(shapes.star(40, 40, 12, '#EF4444', '#B91C1C')),
      d: svgWrap(shapes.star(40, 40, 6, '#EF4444', '#B91C1C')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: tiap baris ukuran bintang membesar (kecil→sedang→besar). Baris 3 kolom 3 = ukuran besar.',
  },
  {
    id: 'MAT-044',
    jenis: 'matriks',
    soal: 'Temukan pola ukuran dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.plus(16, 16, 8, '#F59E0B', '#D97706') +
      shapes.plus(40, 16, 13, '#F59E0B', '#D97706') +
      shapes.plus(64, 16, 18, '#F59E0B', '#D97706') +
      shapes.plus(16, 40, 8, '#F59E0B', '#D97706') +
      shapes.plus(40, 40, 13, '#F59E0B', '#D97706') +
      shapes.plus(64, 40, 18, '#F59E0B', '#D97706') +
      shapes.plus(16, 64, 8, '#F59E0B', '#D97706') +
      shapes.plus(40, 64, 13, '#F59E0B', '#D97706') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.plus(40, 40, 18, '#F59E0B', '#D97706')),
      b: svgWrap(shapes.plus(40, 40, 8, '#F59E0B', '#D97706')),
      c: svgWrap(shapes.plus(40, 40, 18, '#94A3B8', '#64748B')),
      d: svgWrap(shapes.plus(40, 40, 13, '#F59E0B', '#D97706')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris ukuran tanda plus membesar (kecil→sedang→besar). Baris 3 kolom 3 = ukuran besar.',
  },
  {
    id: 'MAT-045',
    jenis: 'matriks',
    soal: 'Temukan pola ukuran dan pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      shapes.circle(16, 16, 5, '#14B8A6', '#0F766E') +
      shapes.circle(40, 16, 8, '#14B8A6', '#0F766E') +
      shapes.circle(64, 16, 11, '#14B8A6', '#0F766E') +
      shapes.circle(16, 40, 5, '#14B8A6', '#0F766E') +
      shapes.circle(40, 40, 8, '#14B8A6', '#0F766E') +
      shapes.circle(64, 40, 11, '#14B8A6', '#0F766E') +
      shapes.circle(16, 64, 5, '#14B8A6', '#0F766E') +
      shapes.circle(40, 64, 8, '#14B8A6', '#0F766E') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 11, '#14B8A6', '#0F766E')),
      b: svgWrap(shapes.circle(40, 40, 8, '#14B8A6', '#0F766E')),
      c: svgWrap(shapes.circle(40, 40, 5, '#14B8A6', '#0F766E')),
      d: svgWrap(shapes.circle(40, 40, 11, '#94A3B8', '#64748B')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: tiap baris ukuran lingkaran membesar (kecil→sedang→besar). Baris 3 kolom 3 = ukuran besar.',
  },
  // ─── MAT-046: soal "transformasi bentuk" orisinal (grid 2x2, bukan ───
  // dari sumber PPT) — pola pengisian bertahap, jawaban benar by
  // construction (progresi sudut 0->90->180->270 derajat).
  {
    id: 'MAT-046',
    jenis: 'matriks',
    soal: 'Perhatikan pola transformasi bidang lingkaran di grid 2x2. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="40" y1="8" x2="40" y2="72" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="8" y1="40" x2="72" y2="40" stroke="#94A3B8" stroke-width="1"/>` +
      pieSlice(24, 24, 14, 0, '#3B82F6', '#1D4ED8') +
      pieSlice(56, 24, 14, 90, '#3B82F6', '#1D4ED8') +
      pieSlice(24, 56, 14, 180, '#3B82F6', '#1D4ED8') +
      shapes.text(56, 56, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(pieSlice(40, 40, 16, 360, '#3B82F6', '#1D4ED8')),
      b: svgWrap(pieSlice(40, 40, 16, 270, '#3B82F6', '#1D4ED8')),
      c: svgWrap(pieSlice(40, 40, 16, 180, '#3B82F6', '#1D4ED8')),
      d: svgWrap(pieSlice(40, 40, 16, 270, '#94A3B8', '#64748B')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: bagian lingkaran yang terisi bertambah 90 derajat tiap langkah (kosong→1/4→1/2→?). Kotak ke-4 = 3/4 terisi (270 derajat).',
  },
];

// ─── 4. DERET GAMBAR ─────────────────────────────────────────
export const soalDeretGambar = [
  {
    id: 'DGM-001',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola deret gambar berikut!',
    stimulus: svgWrap(
      shapes.circle(12, 40, 8, '#3B82F6') +
      shapes.circle(30, 40, 10, '#3B82F6') +
      shapes.circle(50, 40, 12, '#3B82F6') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 14, '#3B82F6')),
      b: svgWrap(shapes.circle(40, 40, 12, '#3B82F6')),
      c: svgWrap(shapes.circle(40, 40, 14, '#EF4444')),
      d: svgWrap(shapes.rect(40, 40, 24, 24, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: lingkaran biru bertambah besar setiap langkah (+2). Berikutnya r=14',
  },
  {
    id: 'DGM-002',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola deret berikut!',
    stimulus: svgWrap(
      shapes.triUp(12, 40, 8, '#EF4444') +
      shapes.triRight(30, 40, 8, '#EF4444') +
      shapes.triDown(50, 40, 8, '#EF4444') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triLeft(40, 40, 16, '#EF4444')),
      b: svgWrap(shapes.triUp(40, 40, 16, '#EF4444')),
      c: svgWrap(shapes.triDown(40, 40, 16, '#EF4444')),
      d: svgWrap(shapes.triRight(40, 40, 16, '#EF4444')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: segitiga berputar 90° CW tiap langkah. Atas→Kanan→Bawah→Kiri',
  },
  {
    id: 'DGM-003',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola deret berikut!',
    stimulus: svgWrap(
      shapes.dot(10, 40, 3, '#374151') +
      shapes.dot(23, 40, 3, '#374151') + shapes.dot(30, 40, 3, '#374151') +
      shapes.dot(43, 38, 3, '#374151') + shapes.dot(47, 40, 3, '#374151') + shapes.dot(43, 42, 3, '#374151') +
      shapes.text(65, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.dot(34, 38, 3) + shapes.dot(38, 35, 3) + shapes.dot(42, 38, 3) + shapes.dot(38, 42, 3)),
      b: svgWrap(shapes.dot(34, 40, 3) + shapes.dot(40, 40, 3) + shapes.dot(46, 40, 3)),
      c: svgWrap(shapes.dot(37, 40, 3) + shapes.dot(43, 40, 3)),
      d: svgWrap(shapes.dot(40, 40, 3)),
    },
    jawaban: 'a',
    pembahasan: 'Pola: 1→2→3→4 titik. Berikutnya 4 titik membentuk belah ketupat',
  },
  {
    id: 'DGM-004',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola warna deret berikut!',
    stimulus: svgWrap(
      shapes.rect(12, 40, 14, 14, '#3B82F6') +
      shapes.rect(30, 40, 14, 14, '#10B981') +
      shapes.rect(48, 40, 14, 14, '#EF4444') +
      shapes.text(68, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 22, 22, '#3B82F6')),
      b: svgWrap(shapes.rect(40, 40, 22, 22, '#10B981')),
      c: svgWrap(shapes.rect(40, 40, 22, 22, '#EF4444')),
      d: svgWrap(shapes.rect(40, 40, 22, 22, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola warna: Biru→Hijau→Merah→Biru (berulang). Berikutnya = Biru',
  },
  {
    id: 'DGM-005',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola bentuk dan posisi berikut!',
    stimulus: svgWrap(
      shapes.circle(12, 20, 7, '#8B5CF6') +
      shapes.circle(30, 35, 7, '#8B5CF6') +
      shapes.circle(48, 50, 7, '#8B5CF6') +
      shapes.text(68, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 60, 14, '#8B5CF6')),
      b: svgWrap(shapes.circle(40, 40, 14, '#8B5CF6')),
      c: svgWrap(shapes.circle(40, 65, 14, '#EF4444')),
      d: svgWrap(shapes.circle(40, 20, 14, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: lingkaran bergerak diagonal ke bawah-kanan (+15 setiap langkah). Berikutnya y≈65',
  },
  {
    id: 'DGM-006',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola deret bentuk berikut!',
    stimulus: svgWrap(
      shapes.triUp(12, 40, 8, '#F59E0B') +
      shapes.rect(30, 40, 14, 14, '#F59E0B') +
      shapes.circle(48, 40, 7, '#F59E0B') +
      shapes.text(68, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diamond(40, 40, 14, '#F59E0B')),
      b: svgWrap(shapes.triUp(40, 40, 14, '#F59E0B')),
      c: svgWrap(shapes.rect(40, 40, 22, 22, '#F59E0B')),
      d: svgWrap(shapes.star(40, 40, 14, '#F59E0B')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: 3 sisi → 4 sisi → 0 sisi (lingkaran) → belah ketupat (4 sisi namun berbeda)',
  },
  {
    id: 'DGM-007',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola garis diagonal berikut!',
    stimulus: svgWrap(
      shapes.diagLine(5, 5, 20, 20, '#374151') +
      shapes.diagLine(5, 5, 35, 35, '#374151') +
      shapes.diagLine(5, 5, 50, 50, '#374151') +
      shapes.text(68, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diagLine(5, 5, 65, 65, '#374151')),
      b: svgWrap(shapes.diagLine(5, 5, 50, 50, '#374151')),
      c: svgWrap(shapes.diagLine(5, 65, 65, 5, '#374151')),
      d: svgWrap(shapes.diagLine(5, 5, 75, 75, '#374151')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: garis diagonal bertambah panjang setiap +15. Berikutnya ujung di (65,65)',
  },
  {
    id: 'DGM-008',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola perubahan ukuran dan isi berikut!',
    stimulus: svgWrap(
      `<circle cx="12" cy="40" r="8" fill="none" stroke="#374151" stroke-width="2"/>` +
      `<circle cx="32" cy="40" r="8" fill="#374151" stroke="#374151" stroke-width="2"/>` +
      `<circle cx="52" cy="40" r="8" fill="none" stroke="#374151" stroke-width="2"/>` +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<circle cx="40" cy="40" r="15" fill="#374151" stroke="#374151" stroke-width="2"/>`),
      b: svgWrap(`<circle cx="40" cy="40" r="15" fill="none" stroke="#374151" stroke-width="2"/>`),
      c: svgWrap(shapes.rect(40, 40, 24, 24, '#374151')),
      d: svgWrap(`<circle cx="40" cy="40" r="15" fill="#3B82F6" stroke="#374151" stroke-width="2"/>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola bergantian: kosong→isi→kosong→isi. Berikutnya = isi (hitam)',
  },
  {
    id: 'DGM-009',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola deret gambar berikut!',
    stimulus: svgWrap(
      shapes.circle(12, 40, 16, '#3B82F6') +
      shapes.circle(30, 40, 12, '#3B82F6') +
      shapes.circle(50, 40, 8, '#3B82F6') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 4, '#3B82F6')),
      b: svgWrap(shapes.circle(40, 40, 8, '#3B82F6')),
      c: svgWrap(shapes.circle(40, 40, 12, '#3B82F6')),
      d: svgWrap(shapes.circle(40, 40, 4, '#EF4444')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: lingkaran biru mengecil setiap langkah (-4). Berikutnya r=4',
  },
  {
    id: 'DGM-010',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola warna deret berikut!',
    stimulus: svgWrap(
      shapes.rect(12, 40, 14, 14, '#3B82F6') +
      shapes.rect(30, 40, 14, 14, '#EF4444') +
      shapes.rect(48, 40, 14, 14, '#3B82F6') +
      shapes.text(68, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 22, 22, '#EF4444')),
      b: svgWrap(shapes.rect(40, 40, 22, 22, '#3B82F6')),
      c: svgWrap(shapes.rect(40, 40, 22, 22, '#10B981')),
      d: svgWrap(shapes.rect(40, 40, 22, 22, '#8B5CF6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola warna berselang-seling: Biru→Merah→Biru→Merah. Berikutnya = Merah',
  },
  {
    id: 'DGM-011',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola ukuran deret berikut!',
    stimulus: svgWrap(
      shapes.circle(12, 40, 6, '#8B5CF6') +
      shapes.circle(30, 40, 9, '#8B5CF6') +
      shapes.circle(50, 40, 12, '#8B5CF6') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.circle(40, 40, 15, '#8B5CF6')),
      b: svgWrap(shapes.circle(40, 40, 12, '#8B5CF6')),
      c: svgWrap(shapes.circle(40, 40, 18, '#8B5CF6')),
      d: svgWrap(shapes.circle(40, 40, 15, '#EF4444')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: lingkaran ungu bertambah besar setiap langkah (+3). Berikutnya r=15',
  },
  {
    id: 'DGM-012',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola rotasi deret berikut!',
    stimulus: svgWrap(
      shapes.triUp(12, 40, 8, '#14B8A6') +
      shapes.triLeft(30, 40, 8, '#14B8A6') +
      shapes.triDown(50, 40, 8, '#14B8A6') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.triRight(40, 40, 16, '#14B8A6')),
      b: svgWrap(shapes.triUp(40, 40, 16, '#14B8A6')),
      c: svgWrap(shapes.triLeft(40, 40, 16, '#14B8A6')),
      d: svgWrap(shapes.triDown(40, 40, 16, '#14B8A6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: segitiga berputar 90° berlawanan jarum jam tiap langkah. Atas→Kiri→Bawah→Kanan',
  },
  {
    id: 'DGM-013',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola jumlah titik berikut!',
    stimulus: svgWrap(
      `<text x="14" y="44" text-anchor="middle" font-size="14" fill="#374151">•</text>` +
      `<text x="34" y="44" text-anchor="middle" font-size="14" fill="#374151">•••</text>` +
      `<text x="54" y="44" text-anchor="middle" font-size="14" fill="#374151">•••••</text>` +
      shapes.text(72, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(`<text x="40" y="44" text-anchor="middle" font-size="14" fill="#374151">•••••••</text>`),
      b: svgWrap(`<text x="40" y="44" text-anchor="middle" font-size="14" fill="#374151">••••••</text>`),
      c: svgWrap(`<text x="40" y="44" text-anchor="middle" font-size="14" fill="#374151">•••••</text>`),
      d: svgWrap(`<text x="40" y="44" text-anchor="middle" font-size="14" fill="#374151">•••••••••</text>`),
    },
    jawaban: 'a',
    pembahasan: 'Pola: jumlah titik ganjil bertambah 2 (1,3,5,...). Berikutnya = 7 titik',
  },
  {
    id: 'DGM-014',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola orientasi kotak berikut!',
    stimulus: svgWrap(
      shapes.rect(12, 40, 18, 10, '#F59E0B') +
      shapes.rect(30, 40, 10, 18, '#F59E0B') +
      shapes.rect(50, 40, 18, 10, '#F59E0B') +
      shapes.text(70, 40, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.rect(40, 40, 14, 24, '#F59E0B')),
      b: svgWrap(shapes.rect(40, 40, 24, 14, '#F59E0B')),
      c: svgWrap(shapes.rect(40, 40, 18, 18, '#F59E0B')),
      d: svgWrap(shapes.rect(40, 40, 14, 24, '#3B82F6')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: kotak berganti-ganti orientasi lebar-tinggi. Berikutnya = orientasi tinggi (tegak)',
  },
  {
    id: 'DGM-015',
    jenis: 'deret_gambar',
    soal: 'Lanjutkan pola panjang garis diagonal berikut!',
    stimulus: svgWrap(
      shapes.diagLine(5, 5, 65, 65, '#374151') +
      shapes.diagLine(5, 5, 50, 50, '#374151') +
      shapes.diagLine(5, 5, 35, 35, '#374151') +
      shapes.text(68, 20, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(shapes.diagLine(5, 5, 20, 20, '#374151')),
      b: svgWrap(shapes.diagLine(5, 5, 35, 35, '#374151')),
      c: svgWrap(shapes.diagLine(5, 5, 50, 50, '#374151')),
      d: svgWrap(shapes.diagLine(5, 65, 65, 5, '#374151')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: garis diagonal memendek setiap langkah (-15). Berikutnya ujung di (20,20)',
  },
  {
    id: 'DGM-016',
    jenis: 'deret_gambar',
    soal: 'Perhatikan pola jumlah dan susunan tekstur berikut. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      clusterDots(16, 16, 1, '#3B82F6') +
      clusterDots(40, 16, 2, '#3B82F6') +
      clusterDots(64, 16, 3, '#3B82F6') +
      clusterDots(16, 40, 2, '#3B82F6') +
      clusterDots(40, 40, 3, '#3B82F6') +
      clusterDots(64, 40, 4, '#3B82F6') +
      clusterDots(16, 64, 3, '#3B82F6') +
      clusterDots(40, 64, 4, '#3B82F6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(clusterDots(40, 40, 5, '#3B82F6')),
      b: svgWrap(clusterDots(40, 40, 4, '#3B82F6')),
      c: svgWrap(clusterDots(40, 40, 3, '#3B82F6')),
      d: svgWrap(clusterDots(40, 40, 5, '#94A3B8')),
    },
    jawaban: 'a',
    pembahasan: 'Pola: jumlah elemen di tiap kotak = (nomor baris + nomor kolom - 1). Kotak baris-3 kolom-3 = 3+3-1 = 5 elemen.',
  },
  {
    id: 'DGM-017',
    jenis: 'deret_gambar',
    soal: 'Perhatikan pola jumlah dan susunan tekstur berikut. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      clusterSquares(16, 16, 1, '#10B981') +
      clusterSquares(40, 16, 2, '#10B981') +
      clusterSquares(64, 16, 3, '#10B981') +
      clusterSquares(16, 40, 2, '#10B981') +
      clusterSquares(40, 40, 3, '#10B981') +
      clusterSquares(64, 40, 4, '#10B981') +
      clusterSquares(16, 64, 3, '#10B981') +
      clusterSquares(40, 64, 4, '#10B981') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(clusterSquares(40, 40, 4, '#10B981')),
      b: svgWrap(clusterSquares(40, 40, 5, '#10B981')),
      c: svgWrap(clusterSquares(40, 40, 5, '#94A3B8')),
      d: svgWrap(clusterSquares(40, 40, 3, '#10B981')),
    },
    jawaban: 'b',
    pembahasan: 'Pola: jumlah elemen di tiap kotak = (nomor baris + nomor kolom - 1). Kotak baris-3 kolom-3 = 3+3-1 = 5 elemen.',
  },
  {
    id: 'DGM-018',
    jenis: 'deret_gambar',
    soal: 'Perhatikan pola jumlah dan susunan tekstur berikut. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      clusterTri(16, 16, 1, '#EF4444') +
      clusterTri(40, 16, 2, '#EF4444') +
      clusterTri(64, 16, 3, '#EF4444') +
      clusterTri(16, 40, 2, '#EF4444') +
      clusterTri(40, 40, 3, '#EF4444') +
      clusterTri(64, 40, 4, '#EF4444') +
      clusterTri(16, 64, 3, '#EF4444') +
      clusterTri(40, 64, 4, '#EF4444') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(clusterTri(40, 40, 3, '#EF4444')),
      b: svgWrap(clusterTri(40, 40, 5, '#94A3B8')),
      c: svgWrap(clusterTri(40, 40, 5, '#EF4444')),
      d: svgWrap(clusterTri(40, 40, 4, '#EF4444')),
    },
    jawaban: 'c',
    pembahasan: 'Pola: jumlah elemen di tiap kotak = (nomor baris + nomor kolom - 1). Kotak baris-3 kolom-3 = 3+3-1 = 5 elemen.',
  },
  {
    id: 'DGM-019',
    jenis: 'deret_gambar',
    soal: 'Perhatikan pola jumlah dan susunan tekstur berikut. Pilih yang mengisi kotak "?"',
    stimulus: svgWrap(
      `<line x1="27" y1="5" x2="27" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="54" y1="5" x2="54" y2="75" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="27" x2="75" y2="27" stroke="#94A3B8" stroke-width="1"/>` +
      `<line x1="5" y1="54" x2="75" y2="54" stroke="#94A3B8" stroke-width="1"/>` +
      clusterPlus(16, 16, 1, '#8B5CF6') +
      clusterPlus(40, 16, 2, '#8B5CF6') +
      clusterPlus(64, 16, 3, '#8B5CF6') +
      clusterPlus(16, 40, 2, '#8B5CF6') +
      clusterPlus(40, 40, 3, '#8B5CF6') +
      clusterPlus(64, 40, 4, '#8B5CF6') +
      clusterPlus(16, 64, 3, '#8B5CF6') +
      clusterPlus(40, 64, 4, '#8B5CF6') +
      shapes.text(64, 64, '?', 14, '#64748B')
    ),
    pilihan: {
      a: svgWrap(clusterPlus(40, 40, 5, '#94A3B8')),
      b: svgWrap(clusterPlus(40, 40, 3, '#8B5CF6')),
      c: svgWrap(clusterPlus(40, 40, 4, '#8B5CF6')),
      d: svgWrap(clusterPlus(40, 40, 5, '#8B5CF6')),
    },
    jawaban: 'd',
    pembahasan: 'Pola: jumlah elemen di tiap kotak = (nomor baris + nomor kolom - 1). Kotak baris-3 kolom-3 = 3+3-1 = 5 elemen.',
  },
];

// ─── HELPER FUNCTIONS ─────────────────────────────────────────
function getRandomSoal(arr, n) {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, Math.min(n, arr.length));
}

// Catatan: kategori TIDAK diacak silang lagi (dulu di-shuffle penuh sehingga
// tiap tipe soal bercampur acak dan user gampang bingung). Sekarang tiap
// kategori dikerjakan berurutan per blok — lihat juga divider "Mulai bagian
// ..." di layar soal FiguralModul.jsx.
export function getRandomSoalRotasiBayangan(n = 25) {
  const rot = getRandomSoal(soalRotasi, Math.ceil(n/2)).map(s => ({ ...s }));
  const bay = getRandomSoal(soalBayangan, Math.floor(n/2)).map(s => ({ ...s }));
  return [...rot, ...bay];
}

export function getRandomSoalPolaMatriks(n = 25) {
  const mat = getRandomSoal(soalMatriks, Math.ceil(n/2)).map(s => ({ ...s }));
  const der = getRandomSoal(soalDeretGambar, Math.floor(n/2)).map(s => ({ ...s }));
  return [...mat, ...der];
}

// ─── Mode tunggal (tidak tercampur tipe lain) ─────────────────
export function getRandomSoalRotasi(n = 25) {
  return getRandomSoal(soalRotasi, n).map(s => ({ ...s }));
}

export function getRandomSoalBayangan(n = 25) {
  return getRandomSoal(soalBayangan, n).map(s => ({ ...s }));
}

export function getRandomSoalMatriks(n = 25) {
  return getRandomSoal(soalMatriks, n).map(s => ({ ...s }));
}

export function getRandomSoalDeretGambar(n = 25) {
  return getRandomSoal(soalDeretGambar, n).map(s => ({ ...s }));
}

export function getRandomSoalFigural(mode = 'campuran') {
  if (mode === 'rotasi_bayangan') return getRandomSoalRotasiBayangan(50);
  if (mode === 'pola_matriks') return getRandomSoalPolaMatriks(50);
  const rb = getRandomSoalRotasiBayangan(25);
  const pm = getRandomSoalPolaMatriks(25);
  return [...rb, ...pm];
}

export function hitungSkorFigural(soalList, jawabanUser) {
  let benar = 0, salah = 0, kosong = 0;
  soalList.forEach(soal => {
    const jwb = jawabanUser.find(j => j.id === soal.id)?.jawaban;
    if (!jwb) kosong++;
    else if (jwb === soal.jawaban) benar++; else salah++;
  });
  return { benar, salah, kosong, skor: Math.round((benar / soalList.length) * 100), total: soalList.length };
}

// ===== SOAL TAMBAHAN BARU (Figural) =====
// Soal lama di atas tetap dipertahankan. Soal berikut dibangkitkan dengan
// PRNG ber-seed tetap (hasil selalu identik tiap load, jadi ID stabil untuk
// resume/progres). Semua stimulus memakai bentuk ASIMETRIS (tidak punya sumbu
// simetri), sehingga rotasi dan pencerminan selalu menghasilkan gambar berbeda
// dan tepat satu pilihan yang benar.
(function tambahSoalFiguralBaru() {
  let seed = 20261026;
  const rnd = () => { seed = (seed * 1664525 + 1013904223) % 4294967296; return seed / 4294967296; };
  const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
  const acak = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
  const HURUF = ['a', 'b', 'c', 'd'];

  // Poligon asimetris (koordinat relatif pusat 0,0; rentang ±18)
  const BENTUK = [
    [[-12,-18],[-2,-18],[-2,0],[14,0],[14,12],[-12,12]],
    [[-14,-16],[12,-16],[12,-8],[-4,-8],[-4,-2],[8,-2],[8,6],[-4,6],[-4,16],[-14,16]],
    [[-16,10],[-16,2],[0,2],[0,-14],[8,-14],[8,10]],
    [[-14,-14],[0,-14],[0,-4],[14,-4],[14,6],[4,6],[4,16],[-14,16]],
    [[-10,-18],[10,-6],[2,-6],[2,16],[-10,16]],
    [[-16,-8],[4,-8],[4,-16],[16,0],[4,16],[4,8],[-16,8],[-16,0]],
  ];
  const WARNA = [['#3B82F6', '#1D4ED8'], ['#EF4444', '#B91C1C'], ['#10B981', '#047857'], ['#F59E0B', '#D97706'], ['#8B5CF6', '#6D28D9']];

  function poli(idx, cx, cy, { rot = 0, mx = false, s = 1, warna = WARNA[0] } = {}) {
    const pts = BENTUK[idx].map(([x, y]) => `${x},${y}`).join(' ');
    return `<polygon points="${pts}" fill="${warna[0]}" stroke="${warna[1]}" stroke-width="1.5" stroke-linejoin="round" transform="translate(${cx} ${cy}) rotate(${rot}) scale(${mx ? -s : s} ${s})"/>`;
  }
  const finalisasi = (soal, benarSvg, salahSvg) => {
    const opts = acak([benarSvg, ...salahSvg]);
    const pilihan = {};
    opts.forEach((o, i) => { pilihan[HURUF[i]] = svgWrap(o); });
    return { ...soal, pilihan, jawaban: HURUF[opts.indexOf(benarSvg)] };
  };

  // ── ROTASI ────────────────────────────────────────────────────
  const rotBaru = [];
  for (let i = 0; i < 24; i++) {
    const idx = i % BENTUK.length, warna = pick(WARNA);
    const awal = pick([0, 90, 180, 270]);
    const dRot = pick([90, 180, 270]);
    const akhir = (awal + dRot) % 360;
    const lain = acak([0, 90, 180, 270].filter(a => a !== akhir)).slice(0, 2);
    const opt = (r, mx) => poli(idx, 40, 40, { rot: r, mx, warna });
    rotBaru.push(finalisasi({
      id: `ROT-${String(16 + i).padStart(3, '0')}`,
      jenis: 'rotasi',
      soal: `Objek berikut diputar ${dRot}° searah jarum jam. Pilih hasil yang benar!`,
      stimulus: svgWrap(opt(awal, false)),
      pembahasan: `Putar ${dRot}° searah jarum jam dari posisi awal. Bentuk ini tidak simetris, jadi hanya satu orientasi yang tepat; pilihan lain adalah orientasi lain atau bayangan cerminnya.`,
    }, opt(akhir, false), [opt(akhir, true), opt(lain[0], false), opt(lain[1], false)]));
  }

  // ── BAYANGAN ──────────────────────────────────────────────────
  const bayBaru = [];
  for (let i = 0; i < 24; i++) {
    const idx = (i + 1) % BENTUK.length, warna = pick(WARNA);
    const vertikal = i % 2 === 0;
    const awal = pick([0, 90, 180, 270]);
    const garis = vertikal
      ? `<line x1="40" y1="5" x2="40" y2="75" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`
      : `<line x1="5" y1="40" x2="75" y2="40" stroke="#CBD5E1" stroke-width="1" stroke-dasharray="4"/>`;
    const [x1, y1] = vertikal ? [20, 40] : [40, 20];
    const [x2, y2] = vertikal ? [60, 40] : [40, 60];
    const gbr = (cx, cy, kk, ab) => {
      const pts = BENTUK[idx].map(([x, y]) => `${x},${y}`).join(' ');
      return `<polygon points="${pts}" fill="${warna[0]}" stroke="${warna[1]}" stroke-width="1.5" stroke-linejoin="round" transform="translate(${cx} ${cy}) scale(${kk ? -0.8 : 0.8} ${ab ? -0.8 : 0.8}) rotate(${awal})"/>`;
    };
    const benar = vertikal ? gbr(x2, y2, true, false) : gbr(x2, y2, false, true);
    const salah = [gbr(x2, y2, false, false), gbr(x2, y2, true, true), vertikal ? gbr(x2, y2, false, true) : gbr(x2, y2, true, false)];
    bayBaru.push(finalisasi({
      id: `BAY-${String(15 + i).padStart(3, '0')}`,
      jenis: 'bayangan',
      soal: `Cermin ${vertikal ? 'vertikal' : 'horizontal'} di tengah. Pilih bayangan yang benar!`,
      stimulus: svgWrap(garis + gbr(x1, y1, false, false)),
      pembahasan: `Bayangan pada cermin ${vertikal ? 'vertikal terbalik kiri-kanan' : 'horizontal terbalik atas-bawah'}. Bentuk asimetris sehingga hanya satu pilihan yang cocok.`,
    }, garis + benar, salah.map(g => garis + g)));
  }

  // ── DERET GAMBAR ──────────────────────────────────────────────
  const dgmBaru = [];
  let guard = 0;
  for (let i = 0; dgmBaru.length < 24 && guard++ < 200; i++) {
    const nomor = `DGM-${String(20 + dgmBaru.length).padStart(3, '0')}`;
    if (i % 2 === 0) {
      const idx = i % BENTUK.length, warna = pick(WARNA), langkah = pick([45, 90, 135]);
      const awal = pick([0, 45, 90]);
      const rot = (k) => (awal + langkah * k) % 360;
      if (new Set([rot(2), rot(3), rot(4)]).size < 3) continue;
      const mini = (cx, k) => poli(idx, cx, 40, { rot: rot(k), s: 0.55, warna });
      const kecil = (r, mx) => poli(idx, 40, 40, { rot: r, mx, warna });
      dgmBaru.push(finalisasi({
        id: nomor, jenis: 'deret_gambar', soal: 'Lanjutkan pola deret gambar berikut!',
        stimulus: svgWrap(mini(11, 0) + mini(29, 1) + mini(47, 2) + shapes.text(67, 40, '?', 14, '#64748B')),
        pembahasan: `Bentuk berputar ${langkah}° searah jarum jam tiap langkah.`,
      }, kecil(rot(3), false), [kecil(rot(2), false), kecil(rot(4), false), kecil(rot(3), true)]));
    } else {
      const warna = pick(['#3B82F6', '#EF4444', '#10B981', '#8B5CF6']);
      const fn = pick([clusterDots, clusterSquares, clusterTri, clusterPlus]);
      const naik = rnd() < 0.5;
      const urut = naik ? [1, 2, 3, 4] : [5, 4, 3, 2];
      const cell = (cx, n) => fn(cx, 40, n, warna);
      const alt = naik ? '#F59E0B' : '#14B8A6';
      const salahN = naik ? [5, 3] : [1, 3];
      dgmBaru.push(finalisasi({
        id: nomor, jenis: 'deret_gambar', soal: 'Lanjutkan pola deret gambar berikut!',
        stimulus: svgWrap(cell(11, urut[0]) + cell(29, urut[1]) + cell(47, urut[2]) + shapes.text(67, 40, '?', 14, '#64748B')),
        pembahasan: `Jumlah elemen ${naik ? 'bertambah' : 'berkurang'} satu tiap langkah, warna tetap sama.`,
      }, fn(40, 40, urut[3], warna), [fn(40, 40, salahN[0], warna), fn(40, 40, salahN[1], warna), fn(40, 40, urut[3], alt)]));
    }
  }

  // ── MATRIKS 3x3 ───────────────────────────────────────────────
  const matBaru = [];
  const BENTUK_M = [
    (x, y, c) => shapes.circle(x, y, 8, c[0], c[1]),
    (x, y, c) => shapes.rect(x, y, 16, 16, c[0], c[1]),
    (x, y, c) => shapes.triUp(x, y, 9, c[0], c[1]),
  ];
  const WARNA_M = [['#3B82F6', '#1D4ED8'], ['#EF4444', '#B91C1C'], ['#10B981', '#047857']];
  for (let i = 0; i < 24; i++) {
    let a, b, d, e;
    do { a = pick([1, 2]); b = pick([1, 2]); d = pick([1, 2]); e = pick([1, 2]); } while ((((a * e - b * d) % 3) + 3) % 3 === 0);
    const sOrd = acak([0, 1, 2]), wOrd = acak([0, 1, 2]);
    const sh = (r, c) => sOrd[(a * r + b * c) % 3];
    const wa = (r, c) => wOrd[(d * r + e * c) % 3];
    const kosong = [[2, 2], [2, 1], [1, 2], [0, 2], [2, 0]][i % 5];
    let sel = '';
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) {
      const cx = 16 + c * 24, cy = 16 + r * 24;
      if (r === kosong[0] && c === kosong[1]) { sel += shapes.text(cx, cy, '?', 14, '#64748B'); continue; }
      sel += `<g transform="translate(${cx} ${cy}) scale(0.8)">${BENTUK_M[sh(r, c)](0, 0, WARNA_M[wa(r, c)])}</g>`;
    }
    let grid = '';
    [28, 52].forEach(p => { grid += `<line x1="${p}" y1="4" x2="${p}" y2="76" stroke="#94A3B8" stroke-width="1"/><line x1="4" y1="${p}" x2="76" y2="${p}" stroke="#94A3B8" stroke-width="1"/>`; });
    const bS = sh(kosong[0], kosong[1]), bW = wa(kosong[0], kosong[1]);
    const mk = (s_, w_) => BENTUK_M[s_](40, 40, WARNA_M[w_]);
    matBaru.push(finalisasi({
      id: `MAT-${String(47 + i).padStart(3, '0')}`,
      jenis: 'matriks',
      soal: 'Temukan pola dan lengkapi matriks 3x3 berikut. Pilih yang mengisi kotak "?"',
      stimulus: svgWrap(grid + sel),
      pembahasan: 'Bentuk dan warna masing-masing mengikuti pola tersendiri: tiap baris dan kolom memuat tiap bentuk dan tiap warna tepat satu kali.',
    }, mk(bS, bW), [mk(bS, (bW + 1) % 3), mk((bS + 1) % 3, bW), mk((bS + 2) % 3, (bW + 2) % 3)]));
  }

  const gabung = (arr, baru) => { const ada = new Set(arr.map(s => s.id)); baru.forEach(s => { if (!ada.has(s.id)) arr.push(s); }); };
  gabung(soalRotasi, rotBaru);
  gabung(soalBayangan, bayBaru);
  gabung(soalDeretGambar, dgmBaru);
  gabung(soalMatriks, matBaru);
})();
